<template>
  <div class="txb-app">
    <!-- Sticky top bar -->
    <header class="txb-topbar">
      <button class="txb-icon-btn txb-back" @click="onBack" title="Back to dashboard">
        <i class="fa fa-arrow-left"></i>
      </button>

      <div class="txb-pat" v-if="patient">
        <div class="txb-pat-avatar" :style="patientAvatarStyle">{{ patientInitials }}</div>
        <div class="txb-pat-meta">
          <div class="txb-pat-name">{{ patient.fName }} {{ patient.lName }}</div>
          <div class="txb-pat-sub">
            <span v-if="patient.chartNumber">{{ patient.chartNumber }}</span>
            <span v-if="patient.chartNumber"> · </span>
            <span>P-{{ patient.patNum }}</span>
          </div>
        </div>
      </div>
      <div class="txb-pat" v-else-if="loading">
        <div class="txb-pat-avatar txb-skel"></div>
        <div class="txb-pat-meta">
          <div class="txb-pat-name txb-skel-line"></div>
          <div class="txb-pat-sub txb-skel-line txb-skel-line-sm"></div>
        </div>
      </div>

      <!-- Plan switcher. Was a wrapping grid of one pill per saved plan, capped
           at 84px and scrolled — a scrollbar inside a sticky toolbar, which
           existed because a real patient has 58 saved plans. A scrolling MENU
           is an ordinary interaction; a scrolling toolbar is not. The menu also
           carries dates and status, which the pills never showed. -->
      <TxbMenu v-if="plans.length > 0" align="left" menu-class="txb-plan-menu">
        <template #trigger="{ toggle, open }">
          <button
            class="txb-plan-switch"
            :class="{ active: open }"
            :disabled="loading"
            :title="selectedPlanHeading"
            @click="toggle"
          >
            <span v-if="selectedPlanIsActive" class="txb-active-dot"></span>
            <span class="txb-plan-switch-name">{{ selectedPlanHeading }}</span>
            <span v-if="plans.length > 1" class="txb-plan-switch-count">
              {{ selectedPlanIndex }} of {{ plans.length }}
            </span>
            <i class="fa fa-caret-down"></i>
          </button>
        </template>

        <template #default="{ close }">
          <button
            v-for="plan in sortedPlans"
            :key="plan.treatPlanNum"
            class="txb-plan-item"
            :class="{ active: plan.treatPlanNum === selectedTreatPlanNum }"
            @click="onSelectPlan(plan.treatPlanNum); close();"
          >
            <span class="txb-plan-item-main">
              <span class="txb-plan-item-name">{{ planLabel(plan) }}</span>
              <span class="txb-plan-item-meta">
                <span v-if="plan.status === 1" class="txb-plan-item-badge">Active</span>
                <span v-if="plan.dateTP">{{ formatDate(plan.dateTP) }}</span>
              </span>
            </span>
            <i v-if="plan.treatPlanNum === selectedTreatPlanNum" class="fa fa-check"></i>
          </button>
        </template>
      </TxbMenu>

      <div class="txb-totals" v-if="builder.plan">
        <div class="txb-totals-label">Total</div>
        <div class="txb-totals-value txb-tab-num">{{ formatMoney(visibleTotal) }}</div>
      </div>

      <!-- Sync cluster: save status and Refresh read as ONE unit. They were two
           adjacent grey pills making opposite claims about Open Dental ("Synced
           with OD" vs "checked just now"), which is most of what made this bar
           feel noisy. Both still reserve width so their labels can't reflow the
           action cluster — just far less of it than before. -->
      <div class="txb-sync-cluster">
        <div class="txb-save-indicator" :class="`txb-save-${saveStatus}`" :title="saveTooltip">
          <i class="fa" :class="saveIconClass"></i>
          <span>{{ saveLabel }}</span>
        </div>
        <RefreshPlanButton
          v-if="builder.treatPlanNum"
          :loading="loading"
          :last-sync-at="builder.lastSyncAt"
          :outcome="builder.refreshOutcome"
          :failed-at="refreshFailedAt"
          @refresh="onRefreshFromOd"
        />
      </div>

      <div class="txb-top-actions">
        <!-- Master comments gate: hidden outright, not disabled, when the practice
             has comments turned off (or has no verified sender email). A disabled
             button would advertise a feature the office chose to switch off. -->
        <button
          v-if="commentsFeatureEnabled"
          class="txb-btn"
          :class="{ active: commentsPanelOpen }"
          :disabled="!builder.treatPlanNum"
          @click="onToggleComments"
        >
          <i class="fa fa-comment-o"></i> Comments
          <span v-if="commentsCount > 0" class="txb-btn-count">{{ commentsCount }}</span>
        </button>
        <!-- Photo Attachments. Always available, whether or not this plan is
             set to show photos — the library belongs to the PATIENT, and the
             TC has to be able to build it before deciding which plans use it.
             Gated on patNum, not treatPlanNum, for the same reason. -->
        <button
          class="txb-btn"
          :disabled="!builder.patNum"
          title="Attach intra-oral photos to this patient"
          @click="photosModalOpen = true"
        >
          <i class="fa fa-camera"></i> Attach Photos
          <span v-if="patientPhotoCount > 0" class="txb-btn-count">{{ patientPhotoCount }}</span>
        </button>
        <button class="txb-btn" :disabled="!builder.treatPlanNum" @click="onSendPlan">
          <i class="fa fa-paper-plane"></i> Send
        </button>

        <!-- Split button, tablet-first. The teal body is Present on Tablet
             (the TC's usual hand-off from this screen) and keeps a direct
             click; the caret carries Present in-office.

             ON A TOUCH DEVICE THE WHOLE THING INVERTS. A QR code exists to get
             a plan onto a *different* screen, so offering it as the primary
             action on the tablet you're already holding is a hand-off to
             yourself. There, the button becomes Present in-office with no
             caret at all — the same hide-don't-disable reasoning the dashboard
             drawer uses, applied to whichever action is meaningless on the
             device in hand. -->
        <div class="txb-split">
          <button
            v-if="isTouchDevice"
            class="txb-btn-primary txb-split-main txb-split-main-solo"
            :disabled="!builder.treatPlanNum"
            @click="onPresent"
            title="Open the full-screen chairside presentation"
          >
            <i class="fa fa-tablet"></i> Present in-office
          </button>

          <template v-else>
            <button
              class="txb-btn-primary txb-split-main"
              :disabled="!builder.treatPlanNum"
              @click="showTablet = true"
              title="Show a QR code to open this plan on your tablet"
            >
              <i class="fa fa-qrcode"></i> Present on Tablet
            </button>

            <TxbMenu align="right" menu-class="txb-present-menu">
              <template #trigger="{ toggle, open }">
                <button
                  class="txb-btn-primary txb-split-caret"
                  :class="{ active: open }"
                  :disabled="!builder.treatPlanNum"
                  title="More ways to present"
                  aria-label="More ways to present"
                  @click="toggle"
                >
                  <i class="fa fa-caret-down"></i>
                </button>
              </template>

              <template #default="{ close }">
                <button class="txb-menu-item" @click="onPresent(); close();">
                  <i class="fa fa-tablet"></i>
                  <span class="txb-menu-item-main">
                    <span class="txb-menu-item-name">Present in-office</span>
                    <span class="txb-menu-item-sub">Full-screen chairside presentation on this screen</span>
                  </span>
                </button>
              </template>
            </TxbMenu>
          </template>
        </div>
      </div>
    </header>

    <!-- Error banner -->
    <div v-if="error" class="txb-error-banner">
      {{ error }}
      <button class="txb-icon-btn ml-auto" @click="clearError"><i class="fa fa-times"></i></button>
    </div>

    <!-- Slice 10a #8 — Concurrent-edit conflict banner. Shown when
         saveAnnotations returns 409 (another TC edited after this caller
         loaded). Two actions: "View their changes" adopts the persisted
         state in-place; "Dismiss" keeps the local edits but the next save
         will hit 409 again until the TC reloads. -->
    <div v-if="conflict" class="txb-conflict-banner">
      <i class="fa fa-exclamation-circle"></i>
      <div class="txb-conflict-body">
        <div class="txb-conflict-title">{{ conflict.message }}</div>
        <div class="txb-conflict-sub">Your local edits weren't saved.</div>
      </div>
      <div class="txb-conflict-actions">
        <button class="txb-btn txb-btn-mini" @click="onAdoptConflict">
          <i class="fa fa-refresh"></i> View their changes
        </button>
        <button class="txb-icon-btn" @click="onDismissConflict" title="Dismiss">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Slice 10a #9 — Failed signed-PDF retry banner. Shown when the
         most recent signed PDF for this plan failed to render (Lambda
         worker dispatch failed or worker errored out). Patient already
         saw the "we're sending this" success page; this is the TC's
         signal that the PDF didn't actually land. One-click retry. -->
    <div v-if="latestSignedPdf && latestSignedPdf.status === 'failed'" class="txb-pdf-retry-banner">
      <i class="fa fa-file-pdf-o"></i>
      <div class="txb-pdf-retry-body">
        <div class="txb-pdf-retry-title">Signed plan PDF didn't render</div>
        <div class="txb-pdf-retry-sub">
          The patient signed this plan but the PDF never reached your imaging.
          <span v-if="latestSignedPdf.processingError"> ({{ latestSignedPdf.processingError }})</span>
        </div>
      </div>
      <button
        class="txb-btn txb-btn-mini"
        :disabled="retryingSignedPdf"
        @click="onRetrySignedPdf"
      >
        <i class="fa" :class="retryingSignedPdf ? 'fa-circle-o-notch fa-spin' : 'fa-refresh'"></i>
        {{ retryingSignedPdf ? 'Retrying…' : 'Retry PDF' }}
      </button>
    </div>

    <!-- 3-pane workspace -->
    <div class="txb-workspace" :style="paneGridStyle">
      <BuilderLeftPane
        :loading="loading"
        :procedures="builder.procedures"
        :visible-procedures="visibleProcedures"
        :priorities="builder.priorities"
        :hidden-priority-def-nums="builder.hiddenPriorityDefNums"
        :group-by="builder.groupBy"
        :new-proc-nums="builder.newProcNums"
        :alts-by-primary="altsByPrimary"
        :show-diagnostic="showDiagnostic"
        :removed-procedures="removedProcedures"
        @reorder="onReorder"
        @set-hidden-priorities="onSetHiddenPriorities"
        @set-group-by="onSetGroupBy"
        @acknowledge-new="acknowledgeNewProcedure"
        @open-alt-picker="onOpenAltPicker"
        @detach-alt="onDetachAlt"
        @remove-procedure="onRemoveProcedure"
        @restore-procedure="onRestoreProcedure"
        @toggle-diagnostic="onToggleDiagnostic"
      />

      <div class="txb-resizer" @mousedown="startResize('left')"></div>

      <main class="txb-center">
        <div class="txb-pane-header">
          <span class="txb-pane-label">PATIENT VIEW</span>
          <div v-if="enabledViewOptions.length > 1" class="txb-view-switcher">
            <button
              v-for="v in enabledViewOptions"
              :key="v.id"
              class="txb-view-tab"
              :class="{ active: activeView === v.id }"
              @click="activeView = v.id"
            >{{ v.label }}</button>
          </div>
          <div class="txb-pane-livehint">
            <i class="fa fa-eye"></i>
            <span>What the patient will see · live</span>
          </div>
        </div>

        <div class="txb-center-scroll">
          <div class="txb-paper">
            <!-- Practice header. When the practice has uploaded a logo
                 (teamio.sites.LogoUrl), show it enlarged and skip the
                 separate name + tagline lines — most logos already
                 contain the practice name. Without a logo, fall back to
                 the initial-circle + name + tagline layout. -->
            <div class="txb-paper-header">
              <div class="txb-practice">
                <img
                  v-if="practiceLogoUrl"
                  :src="practiceLogoUrl"
                  :alt="practiceName"
                  class="txb-practice-logo-large"
                />
                <template v-else>
                  <div class="txb-practice-logo">{{ practiceInitial }}</div>
                  <div class="txb-practice-meta">
                    <div class="txb-practice-name">{{ practiceName }}</div>
                    <div class="txb-practice-tag" v-if="practiceTagline">{{ practiceTagline }}</div>
                  </div>
                </template>
              </div>
              <div class="txb-paper-meta">
                <div class="txb-paper-ref">PatNum: {{ patient ? patient.patNum : '—' }}</div>
              </div>
            </div>

            <div class="txb-paper-greet" v-if="patient">
              Dear {{ patient.fName }},
            </div>
            <div class="txb-paper-intro">
              Here is the treatment plan we discussed. Each item below
              includes a brief explanation of why we're recommending it.
            </div>

            <component
              :is="activeViewComponent"
              :procedures="visibleProcedures"
              :alts-by-primary="altsByPrimary"
              :label-fn="resolveLabel"
              :reason-fn="resolveReason"
              :show-prices="currentViewShowsPrices"
              :pricing-layers="pricingLayers"
              :group-by-priority="itemizedGroupByPriority"
              :group-label="itemizedGroupLabel"
              :show-group-subtotals="itemizedGroupSubtotals"
              :photo-teeth="photoTeeth"
              @open-tooth-photos="onOpenToothPhotos"
            />

            <!-- Plan-level financial summary. Renders below whichever view is
                 active. Gated on currentViewShowsTotals, NOT ShowsPrices —
                 the 'total' mode is exactly "no per-row fees, keep this card",
                 so the two gates must stay separate.
                 The membership/discount layer toggle controls whether the
                 practice discount appears as its own line. -->
            <PlanFinancialSummary
              :procedures="visibleProcedures"
              :show-prices="currentViewShowsTotals"
              :show-discount="pricingLayers.membership"
              :show-balance="showBalance"
              :family-balance="familyBalance"
            />
          </div>
        </div>
      </main>

      <div class="txb-resizer" @mousedown="startResize('right')"></div>

      <aside class="txb-right">
        <nav class="txb-tabs">
          <button
            class="txb-tab"
            :class="{ active: activeRightTab === 'financials' }"
            @click="activeRightTab = 'financials'"
          >Financials</button>
          <button
            class="txb-tab"
            :class="{ active: activeRightTab === 'reasons' }"
            @click="activeRightTab = 'reasons'"
          >Reasons</button>
          <button
            class="txb-tab"
            :class="{ active: activeRightTab === 'notes' }"
            @click="activeRightTab = 'notes'"
          >Notes<span
            v-if="hasInternalNote"
            class="txb-tab-dot"
            title="This plan has an internal note"
          ></span></button>
        </nav>
        <div class="txb-right-body tx-scroll">
          <!-- Financials tab -->
          <div v-show="activeRightTab === 'financials'">
            <!-- Per-view pricing mode. "Total only" is the middle ground a
                 client asked for: keep the plan total, drop the per-procedure
                 fees, so Arch & Region can be the big-picture conversation and
                 Itemized the line-by-line one. Persisted per-patient. -->
            <div class="txb-right-section">
              <div class="txb-right-label">Pricing shown</div>
              <div v-for="v in PRICING_VIEWS" :key="'pm-' + v.id" class="txb-pricing-mode">
                <div class="txb-pricing-mode-view">{{ v.label }}</div>
                <div class="txb-segmented">
                  <button
                    v-for="m in PRICING_MODE_OPTIONS"
                    :key="v.id + '-' + m.id"
                    type="button"
                    class="txb-segmented-btn"
                    :class="{ active: viewPricingModes[v.id] === m.id }"
                    :title="m.hint"
                    @click="onSetPricingMode(v.id, m.id)"
                  >{{ m.label }}</button>
                </div>
              </div>
            </div>

            <!-- Layer breakdown. Each layer is independent OF THE OTHERS, not
                 per view — these say which sub-lines appear wherever a view is
                 set to Full pricing. Which views show money is the control
                 above. Misreading "independent" as "per view" is exactly what
                 prompted the Total-only mode. -->
            <div class="txb-right-section">
              <div class="txb-right-label">Fee breakdown lines</div>
              <div class="txb-right-hint">Applies wherever a view is set to Full pricing.</div>
              <label class="txb-toggle-row">
                <span class="txb-switch">
                  <input
                    type="checkbox"
                    :checked="pricingLayers.netFee"
                    @change="onTogglePricingLayer('netFee')"
                  />
                  <span class="txb-switch-slider"></span>
                </span>
                <span class="txb-toggle-text">Net Fee <span class="txb-toggle-sub">(PPO Fee)</span></span>
              </label>
              <label class="txb-toggle-row">
                <span class="txb-switch">
                  <input
                    type="checkbox"
                    :checked="pricingLayers.standardFee"
                    @change="onTogglePricingLayer('standardFee')"
                  />
                  <span class="txb-switch-slider"></span>
                </span>
                <span class="txb-toggle-text">Standard fee <span class="txb-toggle-sub">(UCR)</span></span>
              </label>
              <label class="txb-toggle-row">
                <span class="txb-switch">
                  <input
                    type="checkbox"
                    :checked="pricingLayers.insuranceEstimate"
                    @change="onTogglePricingLayer('insuranceEstimate')"
                  />
                  <span class="txb-switch-slider"></span>
                </span>
                <span class="txb-toggle-text">Insurance estimate</span>
              </label>
              <label class="txb-toggle-row">
                <span class="txb-switch">
                  <input
                    type="checkbox"
                    :checked="pricingLayers.membership"
                    @change="onTogglePricingLayer('membership')"
                  />
                  <span class="txb-switch-slider"></span>
                </span>
                <span class="txb-toggle-text">Show Procedure Discounts <span class="txb-toggle-sub">(OD Discount column)</span></span>
              </label>
              <!-- Guarantor account balance / credit as a Plan Summary line,
                   folded into the Estimated Patient Portion. -->
              <label class="txb-toggle-row">
                <span class="txb-switch">
                  <input
                    type="checkbox"
                    :checked="showBalance"
                    @change="onToggleShowBalance"
                  />
                  <span class="txb-switch-slider"></span>
                </span>
                <span class="txb-toggle-text">Show balance/credits from Open Dental</span>
              </label>
              <div class="txb-toggle-desc">This is the balance listed under the Guarantor of the Account in Open Dental</div>
              <!-- Per-group subtotals — only meaningful when the Itemized view
                   is grouped by priority; greyed out otherwise. -->
              <label class="txb-toggle-row" :class="{ 'txb-toggle-row-disabled': !itemizedGroupByPriority }">
                <span class="txb-switch">
                  <input
                    type="checkbox"
                    :disabled="!itemizedGroupByPriority"
                    :checked="itemizedGroupSubtotals"
                    @change="onToggleGroupSubtotals"
                  />
                  <span class="txb-switch-slider"></span>
                </span>
                <span class="txb-toggle-text">Per-group subtotals
                  <span class="txb-toggle-sub">Itemized · grouped</span>
                </span>
              </label>
            </div>

            <!-- Photos. Attached to the PATIENT, not this plan — so the toggle
                 is about whether THIS plan shows them, and the library itself
                 is managed from the Attach Photos button up top. -->
            <div class="txb-right-section">
              <div class="txb-right-label">Photos</div>
              <label class="txb-toggle-row">
                <span class="txb-switch">
                  <input
                    type="checkbox"
                    :checked="showPhotos"
                    @change="onToggleShowPhotos"
                  />
                  <span class="txb-switch-slider"></span>
                </span>
                <span class="txb-toggle-text">Show Photos in Tx Plan</span>
              </label>
              <div class="txb-toggle-desc" v-if="patientPhotoCount === 0">
                No photos attached yet — use <strong>Attach Photos</strong> above.
              </div>
              <div class="txb-toggle-desc" v-else>
                {{ planPhotoCount }} of {{ patientPhotoCount }}
                {{ patientPhotoCount === 1 ? 'photo' : 'photos' }} apply to this plan.
                A photo shows when one of its teeth is on the plan; photos with no teeth tagged
                always show.
              </div>
            </div>

            <!-- Itemized-only: group the list by OD priority with per-priority
                 subtotals + choose the group-header word. -->
            <div class="txb-right-section">
              <div class="txb-right-label">Itemized layout</div>
              <label class="txb-toggle-row">
                <span class="txb-switch">
                  <input
                    type="checkbox"
                    :checked="itemizedGroupByPriority"
                    @change="onToggleItemizedGrouping"
                  />
                  <span class="txb-switch-slider"></span>
                </span>
                <span class="txb-toggle-text">Group by priority</span>
              </label>
              <p class="txb-right-hint txb-hint-indent">
                Itemized view only — breaks the plan into Open Dental priority
                groups, each with its own Total Fee / Insurance / Patient subtotal.
              </p>

              <!-- Group-header word: preset pills quick-fill the input; type any
                   custom word directly. Only when grouping is on. -->
              <div v-if="itemizedGroupByPriority" class="txb-grouplabel">
                <div class="txb-grouplabel-caption">Label each group as</div>
                <div class="txb-grouplabel-opts">
                  <button
                    v-for="w in groupLabelPresets"
                    :key="w"
                    type="button"
                    class="txb-grouplabel-pill"
                    :class="{ active: itemizedGroupLabel === w }"
                    @click="onSetGroupLabel(w)"
                  >{{ w }}</button>
                </div>
                <input
                  type="text"
                  class="txb-grouplabel-input"
                  maxlength="24"
                  placeholder="Or type your own word"
                  :value="itemizedGroupLabel"
                  @change="onSetGroupLabel($event.target.value)"
                  @blur="onSetGroupLabel($event.target.value)"
                />
                <div class="txb-grouplabel-preview">
                  Preview: <strong>{{ groupLabelPreview }} 1</strong>, {{ groupLabelPreview }} 2…
                </div>
              </div>
            </div>

            <div class="txb-right-section">
              <div class="d-flex align-items-baseline" style="gap: 8px;">
                <div class="txb-right-label" style="margin-bottom: 0;">Payment options for this patient</div>
                <button
                  v-if="hasPaymentOverrides"
                  class="ml-auto txb-pay-reset"
                  type="button"
                  title="Discard per-patient customizations and revert to your practice defaults"
                  @click="onResetPaymentOverrides"
                >Reset to defaults</button>
              </div>
              <p class="txb-right-hint">
                Pick from your configured options. Adjust discount or terms just for {{ patient ? patient.fName : 'this patient' }} below.
                <span v-if="hasPaymentOverrides" class="txb-pay-customized-pill"><i class="fa fa-pencil"></i> customized</span>
              </p>

              <div
                v-for="(opt, idx) in paymentOptions"
                :key="opt.id || idx"
                class="txb-pay-card"
                :class="{ active: opt.enabled, dim: !opt.enabled }"
              >
                <div class="txb-pay-head">
                  <span class="txb-switch">
                    <input
                      type="checkbox"
                      :checked="opt.enabled"
                      @change="onPaymentOptionChange(idx, 'enabled', $event.target.checked)"
                    />
                    <span class="txb-switch-slider"></span>
                  </span>
                  <span class="txb-pay-title">{{ opt.title }}</span>
                  <span v-if="isCustomOption(opt)" class="txb-pay-custom-badge">Custom</span>
                  <span v-if="isCustomOption(opt)" class="txb-pay-card-actions">
                    <button
                      class="txb-pay-icon"
                      type="button"
                      title="Edit this option"
                      @click="onEditCustomOption(opt)"
                    ><i class="fa fa-pencil"></i></button>
                    <button
                      class="txb-pay-icon txb-pay-icon-danger"
                      type="button"
                      title="Remove this option"
                      @click="onDeleteCustomOption(idx)"
                    ><i class="fa fa-trash"></i></button>
                  </span>
                </div>
                <div class="txb-pay-desc">{{ paymentDesc(opt) }}</div>
                <div v-if="isCustomOption(opt) && opt.terms" class="txb-pay-terms">{{ opt.terms }}</div>

                <div v-if="opt.recommended && opt.enabled" class="txb-pay-pop">
                  <i class="fa fa-star"></i>
                  Marked "Most Popular" on patient view
                </div>

                <div v-if="opt.enabled" class="txb-pay-config">
                  <template v-if="opt.type === 'discount'">
                    <label class="txb-pay-input-label">Discount:</label>
                    <input
                      type="number"
                      :value="opt.discount"
                      class="txb-pay-input"
                      min="0"
                      max="100"
                      @change="onPaymentOptionChange(idx, 'discount', parseFloat($event.target.value) || 0)"
                    />
                    <span class="txb-pay-suffix">%</span>
                  </template>
                  <template v-else-if="opt.type === 'inhouse'">
                    <label class="txb-pay-input-label">Payments:</label>
                    <input
                      type="number"
                      :value="opt.splits"
                      class="txb-pay-input"
                      min="2"
                      max="12"
                      @change="onPaymentOptionChange(idx, 'splits', parseInt($event.target.value, 10) || 2)"
                    />
                  </template>
                  <template v-else-if="opt.type === 'finance'">
                    <!-- Financing tiers are practice-wide (edit in Settings →
                         Payment Options); read-only here so the TC sees the
                         configured menu without per-patient edits. One line per
                         group: label, deferred (0%), then reduced-APR. -->
                    <div class="txb-pay-finance">
                      <div class="txb-pay-finance-label">{{ isCherryBacked(opt) ? 'Financing:' : 'Financing tiers:' }}</div>
                      <div
                        v-for="(line, li) in financeSummaryLines(opt)"
                        :key="li"
                        class="txb-pay-finance-line"
                      >{{ line }}</div>

                      <!-- Cherry-backed: the terms are Cherry's, priced for THIS amount. They
                           re-price themselves when the amount changes; this is the manual
                           re-price, with when Cherry last answered (site timezone). -->
                      <div v-if="isCherryBacked(opt) && cherryPricing && cherryPricing.opt.id === opt.id" class="txb-pay-cherry">
                        <span class="txb-pay-cherry-status">{{ cherryStatusText }}</span>
                        <button
                          type="button"
                          class="txb-pay-cherry-refresh"
                          title="Ask Cherry for fresh terms for this patient's amount"
                          :disabled="cherryTermsFetching || !cherryAmountToPrice"
                          @click="refreshCherryTerms"
                        >
                          <i class="fa fa-refresh" :class="{ 'fa-spin': cherryTermsFetching }"></i>
                          Refresh Cherry terms
                        </button>
                      </div>

                      <!-- The plans Cherry priced for this amount, each with a checkbox: untick one
                           to hide it for THIS patient only (cherryHiddenPlansPatient, saved with the
                           per-patient overrides like the fee). A plan the practice switched off in
                           Settings shows greyed and locked ("Hidden in Settings") - the Builder can
                           hide more, never bring one back. Present Mode, the patient link and the
                           signed PDF render the surviving plans read-only. -->
                      <CherryTierList
                        v-if="isCherryBacked(opt) && cherryPricing && cherryPricing.opt.id === opt.id"
                        variant="builder"
                        selectable
                        :opt="opt"
                        :terms="cherryTerms"
                        :amount="cherryAmountToPrice"
                        :as-of-text="cherryAsOf"
                        :updating="cherryTermsUpdating"
                        @toggle-plan="onCherryPlanToggle(idx, $event)"
                      />
                    </div>
                  </template>
                </div>

                <!-- Per-patient Additional Fee — appears only when the office
                     turned the feature on for this option in Settings. The
                     amount is patient-specific and rides on the same
                     PaymentOptionOverrides channel as discount/splits/months. -->
                <div v-if="opt.enabled && opt.feeEnabled" class="txb-pay-config txb-pay-fee">
                  <label class="txb-pay-input-label">{{ opt.feeDesc || 'Additional fee' }}:</label>
                  <input
                    type="number"
                    :value="opt.feeAmount"
                    class="txb-pay-input"
                    min="0"
                    step="0.01"
                    placeholder="0"
                    @change="onPaymentOptionChange(idx, 'feeAmount', parseFloat($event.target.value) || 0)"
                  />
                  <span class="txb-pay-suffix">{{ (opt.feeUnit || '$') === '%' ? '% of patient portion' : '$' }}</span>
                </div>

                <!-- Per-patient "Pay today" downpayment — appears only when the
                     office turned the feature on for this option in Settings.
                     Subtracts from the split/finance math, so the tiers quote a
                     monthly on the remainder. Set here ahead of the visit and
                     adjustable chairside in Present Mode; same
                     PaymentOptionOverrides channel as the Additional Fee. -->
                <div v-if="opt.enabled && canTakeDownpayment(opt)" class="txb-pay-config txb-pay-fee">
                  <label class="txb-pay-input-label">Pay today (optional):</label>
                  <input
                    type="number"
                    :value="opt.downpayment"
                    class="txb-pay-input"
                    min="0"
                    step="1"
                    placeholder="0"
                    @change="onPaymentOptionChange(idx, 'downpayment', Math.max(0, parseFloat($event.target.value) || 0))"
                  />
                  <span class="txb-pay-suffix">$</span>
                </div>
              </div>

              <button class="txb-pay-add" type="button" @click="onAddCustomOption">
                <i class="fa fa-plus"></i>
                Custom option for {{ patient ? patient.fName : 'patient' }}
              </button>
            </div>
          </div>

          <!-- Reasons tab (Slice 6) -->
          <div v-show="activeRightTab === 'reasons'">
            <div class="txb-right-section">
              <div class="d-flex align-items-baseline" style="gap: 8px;">
                <div class="txb-right-label" style="margin-bottom: 0;">Per-procedure content</div>
                <span class="ml-auto txb-bulk-regen-wrap">
                  <button
                    class="txb-btn txb-btn-mini"
                    :disabled="bulkRegenLabels"
                    @click="onBulkRegen('label')"
                    title="Re-run AI to refresh all labels (overrides existing)"
                  >
                    <i class="fa" :class="bulkRegenLabels ? 'fa-circle-o-notch fa-spin' : 'fa-refresh'"></i>
                    Regen labels
                  </button>
                  <button
                    class="txb-btn txb-btn-mini"
                    :disabled="bulkRegenReasons"
                    @click="onBulkRegen('reason')"
                    title="Re-run AI to refresh all reasons (overrides existing)"
                  >
                    <i class="fa" :class="bulkRegenReasons ? 'fa-circle-o-notch fa-spin' : 'fa-refresh'"></i>
                    Regen reasons
                  </button>
                </span>
              </div>
              <p class="txb-right-hint">
                Edit any field below to override the AI-generated content for this plan only.
                Labels and reasons apply to every procedure with the same code.
                Click the regen icon to ask the AI to try again.
              </p>
              <!-- Only for plans that genuinely had DIFFERENT text on two teeth
                   with the same code — those collapse to one under per-code
                   overrides, so say so instead of changing it silently. -->
              <div v-if="legacyOverrideConflicts.length > 0" class="txb-reason-legacy-warn">
                <i class="fa fa-exclamation-triangle"></i>
                <span>
                  This plan previously had different text on separate teeth for
                  {{ legacyOverrideConflicts.map((c) => c.procCode).join(', ') }}.
                  Content now applies per code — review those rows.
                </span>
              </div>
            </div>

            <!-- Slice 10a #3 — surface AI content load errors. Without
                 this, a Bedrock failure leaves every row rendering its
                 abbrDesc fallback with no signal that AI didn't run.
                 TC silently overrides every row by hand. -->
            <div v-if="aiContentError" class="txb-right-section txb-ai-error">
              <i class="fa fa-exclamation-triangle"></i>
              <div class="txb-ai-error-body">
                <div class="txb-ai-error-title">AI content didn't load</div>
                <div class="txb-ai-error-sub">{{ aiContentError }}</div>
                <button class="txb-btn txb-btn-mini" :disabled="aiContentRetrying" @click="onRetryAiContent">
                  <i class="fa" :class="aiContentRetrying ? 'fa-circle-o-notch fa-spin' : 'fa-refresh'"></i>
                  Retry
                </button>
              </div>
            </div>

            <!-- Grouped exactly like the left pane (same groupProcedures util,
                 same store fields), so a procedure appears where the TC expects
                 it. Ungrouped drag order made procedures effectively unfindable
                 on a 38-row plan — reported as "the Reasons tab is skipping my
                 scheduled procedures" when in fact they were at positions ~33
                 and ~54 of a flat list. -->
            <div v-for="group in reasonGroups" :key="group.key" class="txb-reason-group">
              <div v-if="builder.groupBy !== 'none'" class="txb-reason-group-header">
                <span class="txb-reason-group-name">{{ group.label }}</span>
                <span class="txb-reason-group-count">{{ group.items.length }}</span>
              </div>
            <div
              v-for="proc in group.items"
              :key="proc.procNum"
              class="txb-reason-row"
            >
              <div class="txb-reason-row-head">
                <span class="txb-reason-code">{{ proc.procCode }}</span>
                <span v-if="proc.toothNum" class="txb-reason-tooth">#{{ proc.toothNum }}</span>
                <span class="txb-reason-od">{{ proc.descript }}</span>
              </div>
              <!-- Labels/reasons are per CODE, so an edit here fans out. Say so
                   BEFORE the TC types, not after — the semantics changed. -->
              <div v-if="codeCountMap[proc.codeNum] > 1" class="txb-reason-fanout">
                <i class="fa fa-link"></i>
                Applies to all {{ codeCountMap[proc.codeNum] }} × {{ proc.procCode }} on this plan
              </div>
              <div class="txb-reason-field">
                <label class="txb-reason-label">
                  Label
                  <span v-if="builderHasOverride(proc, 'shortNameOverride')" class="txb-reason-pill">overridden</span>
                </label>
                <div class="txb-reason-input-row">
                  <input
                    type="text"
                    class="txb-reason-input"
                    :value="builderResolvedLabel(proc)"
                    @change="onChangeOverride(proc.procNum, 'shortNameOverride', $event.target.value)"
                    @blur="onBlurOverride(proc, 'shortNameOverride', $event.target.value, builderResolvedLabel(proc))"
                  />
                  <button
                    class="txb-reason-regen"
                    :disabled="isRegen(proc.codeNum, 'label')"
                    :title="builderHasOverride(proc, 'shortNameOverride') ? 'Clear override for all ' + proc.procCode + ' and regenerate' : 'Regenerate with AI'"
                    @click="onRowRegen(proc, 'label')"
                  >
                    <i class="fa" :class="isRegen(proc.codeNum, 'label') ? 'fa-circle-o-notch fa-spin' : 'fa-refresh'"></i>
                  </button>
                </div>
              </div>
              <div class="txb-reason-field">
                <label class="txb-reason-label">
                  Reason (why patient needs this)
                  <span v-if="builderHasOverride(proc, 'reasonOverride')" class="txb-reason-pill">overridden</span>
                </label>
                <div class="txb-reason-input-row">
                  <textarea
                    class="txb-reason-input txb-reason-textarea"
                    rows="2"
                    :value="builderResolvedReason(proc)"
                    @blur="onBlurOverride(proc, 'reasonOverride', $event.target.value, builderResolvedReason(proc))"
                  ></textarea>
                  <button
                    class="txb-reason-regen"
                    :disabled="isRegen(proc.codeNum, 'reason')"
                    :title="builderHasOverride(proc, 'reasonOverride') ? 'Clear override for all ' + proc.procCode + ' and regenerate' : 'Regenerate with AI'"
                    @click="onRowRegen(proc, 'reason')"
                  >
                    <i class="fa" :class="isRegen(proc.codeNum, 'reason') ? 'fa-circle-o-notch fa-spin' : 'fa-refresh'"></i>
                  </button>
                </div>
              </div>
            </div>
            </div>
            <div v-if="visibleProcedures.length === 0" class="txb-right-stub">
              <i class="fa fa-file-text-o"></i>
              <p>No visible procedures to edit content for.</p>
            </div>
          </div>

          <!-- Notes tab -->
          <div v-show="activeRightTab === 'notes'" class="txb-right-section">
            <div class="txb-right-label">Internal note <span class="txb-right-sub">(team-only)</span></div>
            <textarea
              v-model="internalNoteEdit"
              class="txb-note-textarea"
              :class="{ 'txb-note-empty': !internalNoteEdit }"
              placeholder="Add a note for your team — not visible to the patient…"
              rows="6"
              maxlength="20000"
              @blur="onSaveNote"
            ></textarea>
            <p class="txb-right-hint" v-if="noteSaving">Saving…</p>
          </div>
        </div>

        <!-- The save status used to be repeated here, duplicating the top bar's
             sync cluster. Removed 2026-08-05 — one status, next to the actions
             that depend on it. -->
      </aside>
    </div>

    <AltPickerModal
      v-if="altPickerOpen && altPickerPrimary"
      :primary="altPickerPrimary"
      :procedures="altPickerCandidates"
      :priorities="builder.priorities"
      :in-group="proceduresInAnyGroup"
      @close="altPickerOpen = false"
      @attach="onAttachAlts"
    />

    <!-- Remove-procedure confirm (hand-rolled — matches AltPickerModal's
         backdrop/dialog conventions; no BootstrapVue modal in this tree). -->
    <div
      v-if="removeConfirmProc"
      class="rpm-backdrop"
      @click.self="onCancelRemove"
    >
      <div class="rpm-modal" role="dialog" aria-modal="true">
        <header class="rpm-header">
          <div class="rpm-title">Remove {{ resolveLabel(removeConfirmProc) }} from this plan?</div>
          <button class="rpm-close" @click="onCancelRemove" aria-label="Close">
            <i class="fa fa-times"></i>
          </button>
        </header>
        <div class="rpm-body">
          <p class="rpm-text">
            It will no longer appear in this presentation or on the signed PDF. It is not deleted from Open Dental — you can restore it anytime from the "Removed from this plan" section.
          </p>
          <p v-if="removeConfirmProc.isScheduled" class="rpm-note">
            <i class="fa fa-info-circle"></i>
            This procedure is attached to an appointment in Open Dental; the appointment is not affected.
          </p>
          <p v-if="removeConfirmAltCount > 0" class="rpm-note">
            <i class="fa fa-info-circle"></i>
            Its {{ removeConfirmAltCount }} alternative{{ removeConfirmAltCount === 1 ? '' : 's' }} will return to the plan as standalone procedure{{ removeConfirmAltCount === 1 ? '' : 's' }}.
          </p>
        </div>
        <footer class="rpm-footer">
          <button class="rpm-btn" @click="onCancelRemove">Cancel</button>
          <button class="rpm-btn-danger" @click="onConfirmRemove">Remove</button>
        </footer>
      </div>
    </div>

    <SendPlanModal v-if="sendPlanOpen" :patient="patient" :timezone="allSettings && allSettings.GENERAL_TIMEZONE ? allSettings.GENERAL_TIMEZONE.PropertyValue : ''" />

    <!-- Photo Attachments — the patient's photo library and the enlarged
         viewer. Both live here rather than inside the views because the same
         lightbox serves a tooth's camera icon and the manager's thumbnails. -->
    <PatientPhotosModal
      v-if="photosModalOpen && builder.siteID && builder.patNum"
      :site-i-d="builder.siteID"
      :pat-num="builder.patNum"
      :patient="patient"
      @close="photosModalOpen = false"
      @preview="onPreviewPhoto"
    />

    <PhotoLightbox
      v-if="lightboxPhotos.length > 0"
      :photos="lightboxPhotos"
      :start-index="lightboxIndex"
      @close="lightboxPhotos = []"
      @refresh-urls="onRefreshPhotoUrls"
    />

    <!-- Present on Tablet. Purely presentational — no store dispatch and no
         short URL; it string-builds the authenticated Present Mode route and
         QR-codes it. planTotal comes from visibleTotal rather than a stored
         plan total, so the QR card reflects live Builder edits. -->
    <PresentOnTabletModal
      v-if="showTablet && patient && builder.treatPlanNum"
      :pat-num="patient.patNum"
      :treat-plan-num="builder.treatPlanNum"
      :patient-name="`${patient.fName || ''} ${patient.lName || ''}`.trim()"
      :patient-dob="patient.birthdate ? formatDate(patient.birthdate) : ''"
      :plan-heading="selectedPlanHeading"
      :plan-total="formatMoney(visibleTotal)"
      @close="showTablet = false"
    />

    <CustomPaymentOptionModal
      v-if="customOptionModalOpen"
      :patient-name="patient ? patient.fName : ''"
      :option="customOptionEditing"
      @close="onCloseCustomOption"
      @save="onSaveCustomOption"
    />

    <!-- Slice 8a — Comments slide-over panel. Overlays the right pane per BQ1.
         Read-only for now; compose box lands in 8c (TC reply) and AI flow in 8b. -->
    <transition name="txb-comments-slide">
      <!-- commentsFeatureEnabled is checked here too, not just on the button:
           threadBuilder.panelOpen persists in the store across plans and sites,
           so a TC who left the panel open on a comments-enabled site would
           otherwise land on a disabled site with the panel already showing. -->
      <aside v-if="commentsPanelOpen && commentsFeatureEnabled" class="txb-comments-panel">
        <header class="txb-comments-header">
          <h3 class="txb-comments-title">
            <i class="fa fa-comment-o"></i> Comments
            <span v-if="commentsCount > 0" class="txb-comments-count">{{ commentsCount }}</span>
          </h3>
          <button class="txb-icon-btn" @click="onCloseComments" title="Close">
            <i class="fa fa-times"></i>
          </button>
        </header>
        <div class="txb-comments-body">
          <CommentsThread
            :comments="threadBuilder.comments"
            :loading="threadBuilder.loading"
            :error="threadBuilder.error || ''"
            :tc-lookup="tcLookup"
            :timezone="siteTimezone"
            :approving-comment-id="threadBuilder.composing.approvingCommentID"
            viewer-type="admin"
            @replace-ai="onReplaceAi"
            @approve-ai="onApproveAi"
          />
        </div>
        <!-- Slice 8c — TC compose. When `replacing` is set, shows a banner
             that the AI text below is being replaced; the textarea pre-fills
             with the AI's draft so TC can lightly edit. -->
        <footer class="txb-comments-compose">
          <div v-if="threadBuilder.composing.replacing" class="txb-compose-banner">
            <i class="fa fa-pencil"></i>
            <span>Replacing the AI reply with yours.</span>
            <button type="button" class="txb-compose-banner-cancel" @click="onCancelReplace">Cancel</button>
          </div>
          <textarea
            class="txb-compose-textarea"
            :value="threadBuilder.composing.draft"
            :disabled="threadBuilder.composing.sending"
            :placeholder="composePlaceholder"
            rows="3"
            maxlength="4000"
            @input="onTcDraftInput"
            @keydown.meta.enter.prevent="onTcSend"
            @keydown.ctrl.enter.prevent="onTcSend"
          ></textarea>
          <div v-if="threadBuilder.composing.error" class="txb-compose-error">
            <i class="fa fa-exclamation-circle"></i>
            {{ threadBuilder.composing.error }}
            <button type="button" class="txb-compose-error-dismiss" @click="onClearTcComposeError">×</button>
          </div>

          <!-- Pre-emptive notice for a practice with no Teamio texting or verified
               sender: their reply posts to the thread, but nothing reaches the
               patient automatically. Site capability is static, so this is shown
               BEFORE they type rather than only after — telling someone their
               message didn't go out is worse than telling them up front. -->
          <div v-if="!officeCanNotify && !lastPatientNotify" class="txb-notify-note">
            <i class="fa fa-info-circle"></i>
            <span>This account doesn't include Teamio texting. After you reply, copy the message and send it from your texting app.</span>
          </div>

          <!-- Outcome of the last reply's patient notification (from the
               postTcMessage / releaseAiReply response). Previously this was
               discarded, so an undelivered reply still looked like a success. -->
          <div
            v-if="lastPatientNotify"
            class="txb-notify-result"
            :class="'txb-notify-' + notifyBannerTone"
          >
            <i class="fa" :class="notifyBannerIcon"></i>
            <span class="txb-notify-text">{{ notifyBannerText }}</span>
            <button
              v-if="notifyHandoffText"
              type="button"
              class="txb-notify-copy"
              :class="{ 'txb-notify-copy-done': handoffCopied }"
              @click="onCopyHandoff"
            >
              <i class="fa" :class="handoffCopied ? 'fa-check' : 'fa-clipboard'"></i>
              {{ handoffCopied ? 'Copied — paste into your texting app' : 'Copy message + link' }}
            </button>
            <button
              v-else
              type="button"
              class="txb-notify-dismiss"
              aria-label="Dismiss"
              @click="lastPatientNotify = null"
            >×</button>
          </div>
          <div class="txb-compose-actions">
            <span class="txb-compose-hint">⌘/Ctrl + Enter to send</span>
            <button
              class="txb-compose-send"
              type="button"
              :disabled="!canTcSend"
              @click="onTcSend"
            >
              <i v-if="threadBuilder.composing.sending" class="fa fa-circle-o-notch fa-spin"></i>
              <i v-else class="fa fa-paper-plane"></i>
              {{ tcSendLabel }}
            </button>
          </div>
        </footer>
      </aside>
    </transition>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import mixin from '@/mixin.js';
import BuilderLeftPane from './components/BuilderLeftPane.vue';
import AltPickerModal from './components/AltPickerModal.vue';
import SendPlanModal from './components/SendPlanModal.vue';
import CommentsThread from './components/CommentsThread.vue';
import ItemizedView from './components/views/ItemizedView.vue';
import ArchRegionView from './components/views/ArchRegionView.vue';
import PlanFinancialSummary from './components/PlanFinancialSummary.vue';
import CustomPaymentOptionModal from './components/CustomPaymentOptionModal.vue';
import RefreshPlanButton from './components/RefreshPlanButton.vue';
import PresentOnTabletModal from './components/PresentOnTabletModal.vue';
import TxbMenu from './components/TxbMenu.vue';
import { isTouchPrimaryDevice } from '@/utils/deviceType.js';
import { interpolatePaymentDesc, overlayRecommendedFromSettings, canTakeDownpayment, cherryPricingFor } from './utils/paymentOptions.js';
import { isCherryBacked, cherryTermsMatch, cherryAsOfText, normalizeHiddenPlanKeys } from './utils/cherryTerms.js';
import { toggleHiddenPlanKey } from './utils/cherryCatalog.js';
import CherryTierList from './components/CherryTierList.vue';
import { patientPortion as sumPatientPortion } from './utils/patientPortion.js';
import cherryTermsSync from './mixins/cherryTermsSync.js';
import { groupProcedures } from './utils/groupProcedures.js';
import { resolvePricingDisplay, buildPricingModePatch } from './utils/resolveDisplay.js';
import {
  resolveEnabledViews, resolveDefaultView, TX_VIEW_CHOICES, TX_PRICING_MODE_OPTIONS,
} from './constants/views.js';
import PatientPhotosModal from './components/photos/PatientPhotosModal.vue';
import PhotoLightbox from './components/photos/PhotoLightbox.vue';
import {
  planToothNums, filterPhotosForPlan, photoTeethMap, photosForTooth,
} from './utils/photoFilter.js';

export default {
  name: 'TxPresenterBuilder',
  // cherryTermsSync re-prices a Cherry-backed option when the amount it finances changes; it
  // reads the `cherryPricing` computed below.
  mixins: [mixin, cherryTermsSync],
  components: { BuilderLeftPane, AltPickerModal, SendPlanModal, CommentsThread, ItemizedView, ArchRegionView, PlanFinancialSummary, CustomPaymentOptionModal, RefreshPlanButton, PresentOnTabletModal, TxbMenu, PatientPhotosModal, PhotoLightbox, CherryTierList },

  data() {
    return {
      // Outcome of the most recent patient notification (postTcMessage /
      // releaseAiReply). Kept OUT of the store's threadBuilder state on purpose so
      // it can't perturb the optimistic-comment flow
      // (INSERT/REPLACE/REMOVE_OPTIMISTIC_TC_COMMENT).
      lastPatientNotify: null,
      handoffCopied: false,

      // Present-on-Tablet QR modal. Local flag only — the modal makes no store
      // dispatch and needs no short URL.
      showTablet: false,

      // Photo Attachments. `lightboxPhotos` non-empty IS the open state — one
      // source of truth beats a boolean that can disagree with the array.
      photosModalOpen: false,
      lightboxPhotos: [],
      lightboxIndex: 0,
      // Resolved once: a touch-primary device doesn't need a QR hand-off to
      // itself. Hide rather than disable, matching the dashboard drawer.
      isTouchDevice: isTouchPrimaryDevice(),

      // Timestamp of the last failed refresh-from-OD, so the pill can flash an
      // error state. A timestamp rather than a boolean so two consecutive
      // failures each re-trigger the flash.
      refreshFailedAt: null,
      // When the tab was last hidden — see the visibilitychange handler.
      hiddenSince: null,

      plans: [],
      patient: null,
      selectedTreatPlanNum: null,
      paneWidths: {
        left: 320,
        right: 360,
      },
      resizing: null,
      resizeStartX: 0,
      resizeStartWidth: 0,

      activeView: 'region',
      viewOptions: [
        { id: 'region', label: 'Arch & Region' },
        { id: 'itemized', label: 'Itemized' },
      ],

      activeRightTab: 'financials', // 'financials' | 'reasons' | 'notes'
      // NOTE: pricing layers + per-view pricing modes are persisted under
      // annotations.pricingDisplay (per-patient). Read via computeds below,
      // toggled via onTogglePricingLayer / onSetPricingMode which dispatch
      // saveAnnotations using the same conflict-detection plumbing as the
      // rest of the Builder state.

      // Per-patient payment overrides — see `paymentOptions` computed; it
      // now reads `builder.paymentOptionOverrides` (persisted) before
      // falling back to practice defaults.

      internalNoteEdit: '',
      noteSaving: false,
      lastSavedNote: '',

      altPickerOpen: false,
      altPickerPrimary: null,

      // Remove-procedure confirm modal. Holds the proc pending removal (null =
      // closed); confirming dispatches removeProcedure.
      removeConfirmProc: null,

      // Custom per-patient payment option modal. `customOptionEditing` holds
      // the option being edited (null = create mode).
      customOptionModalOpen: false,
      customOptionEditing: null,

      // Slice 10a #3 — AI content retry state.
      aiContentRetrying: false,

      // Slice 8e — focus-only thread polling.
      // Interval handle is non-null only while polling is active. Visibility
      // listener stays bound for the component's lifetime; the interval
      // toggles based on `commentsPanelOpen && !document.hidden`.
      threadPollInterval: null,
      threadPollVisibilityHandler: null,
      // Per-view pricing-mode control. Frozen constants, not reactive state —
      // held here so the template can v-for them.
      PRICING_VIEWS: TX_VIEW_CHOICES,
      PRICING_MODE_OPTIONS: TX_PRICING_MODE_OPTIONS,

      THREAD_POLL_MS: 10000,
      // How long the tab must have been hidden before returning to it counts
      // as "came back from Open Dental" rather than an incidental alt-tab.
      REFOCUS_REFRESH_MIN_AWAY_MS: 20000,
    };
  },

  computed: {
    ...mapState('txPresenter', {
      builder: (s) => s.builder,
      threadBuilder: (s) => s.threadBuilder,
    }),
    commentsPanelOpen() { return this.threadBuilder.panelOpen; },
    commentsCount() { return this.threadBuilder.comments ? this.threadBuilder.comments.length : 0; },

    /**
     * Site timezone for displaying thread timestamps. Sourced from the
     * mixin's allSettings (loaded from teamio.settings on app boot).
     * Falls through to '' (browser local) if missing.
     */
    siteTimezone() {
      const s = this.allSettings && this.allSettings.GENERAL_TIMEZONE;
      return (s && s.PropertyValue) || '';
    },

    /**
     * Slice 8c — map { [EmployeeID]: { initials, fName, lName } } for
     * CommentsThread to resolve admin-side author labels into "NS" / "JD"
     * style initials. Source: customer.employees, where each row has
     * EmployeeID + Name (full name; we split on first space for initials).
     */
    tcLookup() {
      const employees = (this.$store.state.customer && this.$store.state.customer.employees) || [];
      const out = {};
      employees.forEach((e) => {
        if (!e || !e.EmployeeID) return;
        const fullName = String(e.Name || '').trim();
        const parts = fullName.split(/\s+/).filter(Boolean);
        const fName = parts[0] || '';
        const lName = parts.length > 1 ? parts[parts.length - 1] : '';
        const initials = ((fName.charAt(0) || '') + (lName.charAt(0) || '')).toUpperCase();
        out[e.EmployeeID] = { initials, fName, lName };
      });
      return out;
    },

    // Slice 8c — TC compose
    composePlaceholder() {
      return this.threadBuilder.composing.replacing
        ? 'Edit the AI text or rewrite it from scratch…'
        : 'Reply to the patient…';
    },
    canTcSend() {
      const c = this.threadBuilder.composing;
      if (c.sending) return false;
      const trimmed = (c.draft || '').trim();
      return trimmed.length > 0 && trimmed.length <= 4000;
    },
    tcSendLabel() {
      const c = this.threadBuilder.composing;
      if (c.sending) return c.replacing ? 'Replacing…' : 'Sending…';
      return c.replacing ? 'Replace AI reply' : 'Send reply';
    },

    // ─── Patient-notification outcome (Core-plan handoff) ───
    /** Can this practice reach a patient on ANY channel? Static per site. */
    officeCanNotify() {
      if (!this.siteChannelsReady) return true; // unknown ≠ denied; see the store getter
      return !!(this.siteChannels && this.siteChannels.anyAvailable);
    },
    /** Backend-supplied text for the copy handoff — identical to what we'd have texted. */
    notifyHandoffText() {
      const n = this.lastPatientNotify;
      return (n && n.handoff && n.handoff.suggestedText) || '';
    },
    notifyBannerTone() {
      const n = this.lastPatientNotify;
      if (!n) return 'muted';
      if (n.sent) return 'success';
      return n.reason === 'no-channel-available' ? 'warn' : 'muted';
    },
    notifyBannerIcon() {
      const tone = this.notifyBannerTone;
      if (tone === 'success') return 'fa-check-circle';
      return tone === 'warn' ? 'fa-exclamation-triangle' : 'fa-info-circle';
    },
    notifyBannerText() {
      const n = this.lastPatientNotify;
      if (!n) return '';
      if (n.sent) return n.channel === 'sms' ? 'Patient notified by text.' : 'Patient notified by email.';
      switch (n.reason) {
        case 'batched':
          // Deliberately not alarming — this is the 5-minute batch gate working.
          return 'Patient was alerted a few minutes ago — no extra message sent.';
        case 'no-channel-available':
          return "Your reply is posted, but this patient won't get an automatic alert.";
        case 'no-short-url':
          return 'Your reply is posted. Send this patient their plan link so they can see it.';
        default:
          return "Your reply is posted, but we couldn't send the patient an alert.";
      }
    },
    ...mapGetters('txPresenter', [
      'builderVisibleProcedures',
      'builderRemovedProcedures',
      'builderHiddenCount',
      'builderAltsByPrimary',
      'builderProceduresInAnyGroup',
      'builderResolvedLabel',
      'builderResolvedReason',
      'settingsGeneralResolved',
      'settingsPresentationResolved',
      'settingsPaymentResolved',
      'siteChannels',
      'siteChannelsReady',
      'commentsFeatureEnabled',
    ]),

    altsByPrimary() { return this.builderAltsByPrimary; },
    proceduresInAnyGroup() { return this.builderProceduresInAnyGroup; },

    // Removed procedures for the left pane, enriched with a resolved
    // employee name for the "Removed … by <name>" subtitle (the getter
    // only carries the raw EmployeeID).
    removedProcedures() {
      return this.builderRemovedProcedures.map((proc) => ({
        ...proc,
        removedByName: this.resolveEmployeeName(proc.removedBy),
      }));
    },

    // Alt-picker candidates exclude procs already removed from this plan —
    // a removed proc shouldn't be attachable as an alternative.
    altPickerCandidates() {
      const annos = this.builder.annotations || {};
      return this.builder.procedures.filter((p) => {
        const a = annos[p.procNum];
        return !(a && a.removed);
      });
    },

    removeConfirmAltCount() {
      if (!this.removeConfirmProc) return 0;
      const alts = this.altsByPrimary[this.removeConfirmProc.procNum];
      return Array.isArray(alts) ? alts.length : 0;
    },

    bulkRegenLabels() { return !!this.builder.procedureContent.bulkRegenerating.labels; },
    bulkRegenReasons() { return !!this.builder.procedureContent.bulkRegenerating.reasons; },
    // Slice 10a #3 — surface the load error from the store. Drives the
    // Retry banner in the right pane.
    aiContentError() { return this.builder.procedureContent.error; },
    // Slice 10a #8 — concurrent-edit conflict state from the store.
    conflict() { return this.builder.conflict; },
    // Slice 10a #9 — signed-PDF retry banner state.
    latestSignedPdf() { return this.builder.latestSignedPdf; },
    retryingSignedPdf() { return this.builder.retryingSignedPdf; },

    sendPlanOpen() { return this.$store.state.txPresenter.sendPlan.open; },

    // Bound resolvers for view components — defer to store getters which handle
    // the override → AI-cache → fallback priority chain.
    resolveLabel() { return (proc) => this.builderResolvedLabel(proc); },
    resolveReason() { return (proc) => this.builderResolvedReason(proc); },

    /**
     * Resolved payment options for the right pane. Per-patient overrides
     * (persisted in tx_presenter_presentations.PaymentOptionOverrides)
     * take precedence over practice defaults. Returns a deep clone so
     * the template can safely render against array items without
     * accidentally mutating state — all mutations go through explicit
     * @change handlers that build a fresh array and dispatch a save.
     */
    paymentOptions() {
      const blob = this.settingsPaymentResolved;
      const settingsOptions = (blob && blob.options) || [];
      const overrides = this.builder.paymentOptionOverrides;
      const base = Array.isArray(overrides) ? overrides : settingsOptions;
      // Deep clone (template mutates via @change handlers) + always source
      // "Most Popular" from live settings, not a stale override snapshot.
      const cloned = JSON.parse(JSON.stringify(base));
      return overlayRecommendedFromSettings(cloned, settingsOptions);
    },
    hasPaymentOverrides() {
      return Array.isArray(this.builder.paymentOptionOverrides);
    },

    // ─── Cherry (real financing terms) ───────────────────────
    /** The plan-level chosen option + its Pay today, exactly as Present Mode reads them. */
    chosenPaymentOptionId() {
      const pd = (this.builder.annotations && this.builder.annotations.pricingDisplay) || {};
      return pd.chosenPaymentOptionId || null;
    },
    chosenDownpayment() {
      const pd = (this.builder.annotations && this.builder.annotations.pricingDisplay) || {};
      const n = parseFloat(pd.chosenDownpayment);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },
    /** Patient out-of-pocket the payment math runs on - the same sum Present Mode's optBreakdown uses. */
    builderPatientPortion() {
      return sumPatientPortion(this.visibleProcedures, this.showBalance ? this.familyBalance : 0);
    },
    /**
     * The (single) enabled Cherry-backed option and the amount Cherry must price for it. SAME
     * inputs as Present Mode's optBreakdown: portion incl. the balance layer, the option's
     * per-plan fee, and Pay today only for the chosen option. cherryTermsSync watches this.
     */
    cherryPricing() {
      return cherryPricingFor(this.paymentOptions, this.builderPatientPortion, this.chosenPaymentOptionId, this.chosenDownpayment);
    },
    /** "October 10, 2026 at 11:00 AM" - when Cherry priced the terms on screen, in the site's timezone. */
    cherryAsOf() {
      return cherryAsOfText(this.cherryTerms, this.siteTimezone);
    },
    /** One line under the Cherry option: what the terms on hand are for, and when they were fetched. */
    cherryStatusText() {
      if (this.cherryTermsFetching) return 'Updating Cherry terms…';
      const amount = this.cherryAmountToPrice;
      if (amount !== null && cherryTermsMatch(this.cherryTerms, amount)) {
        const when = this.cherryTerms.fetchedAt ? this.formatDateTimeWitTz(this.cherryTerms.fetchedAt, this.siteTimezone) : '';
        return when ? `Cherry terms fetched ${when}` : 'Cherry terms up to date';
      }
      if (!this.cherryEnabled) return 'Cherry financing is not enabled for this site.';
      return this.cherryTermsUpdating ? 'Updating Cherry terms…' : 'No Cherry terms yet for this amount.';
    },

    loading() { return this.builder.loading; },
    error() { return this.builder.error; },
    saveStatus() { return this.builder.saveStatus; },

    visibleProcedures() {
      return this.builderVisibleProcedures;
    },
    /**
     * The Reasons tab's rows, grouped identically to the left pane — same util,
     * same store fields, so it mirrors whatever grouping mode the TC has active
     * (including 'none' → a single group). Without this the tab rendered a flat
     * drag-ordered list and procedures were reported as "missing" when they were
     * simply 30 rows away from where the left pane puts them.
     */
    reasonGroups() {
      return groupProcedures(this.visibleProcedures, this.builder.groupBy, this.builder.priorities);
    },
    /**
     * How many visible procedures share each code — drives the "applies to all
     * N × D2740" hint, since label/reason overrides are per CODE.
     */
    codeCountMap() {
      const m = {};
      this.visibleProcedures.forEach((p) => { m[p.codeNum] = (m[p.codeNum] || 0) + 1; });
      return m;
    },
    legacyOverrideConflicts() {
      return this.$store.getters['txPresenter/builderLegacyOverrideConflicts'];
    },
    visibleTotal() {
      return this.visibleProcedures.reduce((sum, p) => sum + (p.fee || 0), 0);
    },
    activeViewComponent() {
      switch (this.activeView) {
        case 'itemized': return 'ItemizedView';
        case 'region':
        default: return 'ArchRegionView';
      }
    },

    /**
     * Which patient views are enabled (Settings → General), filtered to the
     * canonical tab order. Drives the view switcher; when only one is
     * enabled the switcher is hidden entirely.
     */
    enabledViewOptions() {
      const enabled = resolveEnabledViews(this.settingsPresentationResolved);
      return this.viewOptions.filter((o) => enabled.includes(o.id));
    },

    /**
     * Effective display config for this plan — the per-patient
     * `annotations.pricingDisplay` layered over the practice "house style"
     * defaults (Settings → Presentation), with a hardcoded safety net.
     * Every display computed below reads from this so the precedence is
     * consistent (per-patient override → practice default → hardcoded).
     *
     * `pricingDisplay` lives in the same Annotations JSON column the
     * per-procNum overrides do; existing iterations filter by per-proc
     * fields like altGroupId, so they naturally ignore this entry.
     */
    resolvedDisplay() {
      const pd = (this.builder.annotations && this.builder.annotations.pricingDisplay) || {};
      const defaults = (this.settingsPresentationResolved && this.settingsPresentationResolved.displayDefaults) || {};
      return resolvePricingDisplay(pd, defaults);
    },

    /**
     * Per-view pricing mode: 'full' | 'total' | 'none'. See resolveDisplay.js.
     */
    viewPricingModes() {
      return this.resolvedDisplay.pricing;
    },

    /**
     * Per-row pricing layer toggles. Each layer is independent OF THE OTHERS —
     * they are NOT per-view. They say which sub-lines appear wherever a view is
     * set to 'full'; which views show money at all is viewPricingModes.
     *
     * That ambiguity is what a production client misread in August 2026: they
     * expected the layer toggles to be scoped per view and reported that
     * "toggling the pricing layers shows/hides it for both itemized and non
     * itemized together". The answer to that ask is the 'total' mode, not
     * per-view layers.
     *
     *   netFee — the office-collectable (PPO-aware NetFee). The primary
     *            number an in-office TC walks the patient through.
     *   standardFee — UCR sub-line. Only meaningful when it differs from
     *                 the net (i.e., a contracted-rate adjustment exists).
     *   insuranceEstimate — Insurance contribution sub-line. Only renders
     *                       when the patient has active insurance.
     *   membership — Practice discount sub-line. Hidden in patient view
     *                regardless (internal-only).
     */
    pricingLayers() {
      return this.resolvedDisplay.layers;
    },

    /**
     * PER-ROW fees in the active view. Only 'full' shows them; in 'total' the
     * fee column disappears (and, in Itemized, the per-group subtotals with
     * it) while the Plan Summary card below still carries the plan total.
     */
    currentViewShowsPrices() {
      return this.viewPricingModes[this.activeView] === 'full';
    },

    /**
     * The Plan Summary CARD, which is a separate gate from the row fees —
     * that split is the whole point of the 'total' mode. Binding the card to
     * currentViewShowsPrices (as this did before) is what made "hide per-row
     * fees but keep the total" impossible to express.
     */
    currentViewShowsTotals() {
      return this.viewPricingModes[this.activeView] !== 'none';
    },

    /**
     * Itemized-only: break the list into OD-priority groups with per-priority
     * subtotals. Persisted alongside the other pricingDisplay flags.
     */
    itemizedGroupByPriority() {
      return this.resolvedDisplay.itemizedGroupByPriority;
    },

    /** Group-header word for grouped Itemized ("Priority"/"Visit"/"Phase"/custom). */
    itemizedGroupLabel() {
      return this.resolvedDisplay.itemizedGroupLabel;
    },
    /** Per-group subtotal footer visibility (grouped Itemized). */
    itemizedGroupSubtotals() {
      return this.resolvedDisplay.itemizedGroupSubtotals;
    },
    /**
     * "Show balance/credits from Open Dental" — when on, PlanFinancialSummary
     * renders the Remaining Balance / Credits line and folds familyBalance into
     * the Estimated Patient Portion. Resolved per-patient → practice → hardcoded.
     */
    showBalance() {
      return this.resolvedDisplay.showBalance;
    },

    // ─── Photo Attachments ───────────────────────────────────────────────
    showPhotos() {
      return this.resolvedDisplay.showPhotos;
    },
    /** The whole patient library, regardless of what this plan shows. */
    patientPhotos() {
      return this.$store.state.txPresenter.patientPhotos.list;
    },
    patientPhotoCount() {
      return this.patientPhotos.length;
    },
    /** The teeth this plan actually treats — the filter's other half. */
    planTeeth() {
      return planToothNums(this.visibleProcedures);
    },
    /**
     * The subset of the library this plan shows. Note this ignores showPhotos:
     * the count is what the right panel reports ("3 of 7 apply to this plan"),
     * which stays meaningful — and honest — while photos are toggled off.
     */
    visiblePlanPhotos() {
      return filterPhotosForPlan(this.patientPhotos, this.planTeeth);
    },
    planPhotoCount() {
      return this.visiblePlanPhotos.length;
    },
    /**
     * Camera-icon lookup for the shared views. THIS is where showPhotos is
     * applied — an empty object means the views render no icons at all and
     * never have to know a toggle exists.
     */
    photoTeeth() {
      if (!this.showPhotos) return {};
      return photoTeethMap(this.visiblePlanPhotos, this.planTeeth);
    },
    /** Guarantor account balance / credit (BalTotal − InsEst) from the load. */
    familyBalance() {
      return this.builder.familyBalance || 0;
    },
    groupLabelPresets() { return ['Priority', 'Visit', 'Phase']; },
    groupLabelPreview() {
      return this.itemizedGroupLabel || 'Priority';
    },

    /** Drives the dot on the Notes tab — true when this plan has an internal note. */
    hasInternalNote() {
      return (this.internalNoteEdit || '').trim().length > 0;
    },

    /**
     * Whether diagnostic codes (exams, x-rays — D0xxx) show in the plan.
     * Persisted per-patient; default ON (shown). The actual filtering happens
     * in the builderVisibleProcedures getter so it applies to the left panel,
     * every view, Present Mode, and totals at once.
     */
    showDiagnostic() {
      return this.resolvedDisplay.showDiagnostic;
    },

    site() {
      const sites = (this.$store.state.customer && this.$store.state.customer.sites) || [];
      return sites.find((s) => s.SiteID === this.siteID) || null;
    },
    practiceName() {
      // Settings practiceName takes precedence; falls back to teamio.sites.Name; then a generic label.
      const fromSettings = (this.settingsGeneralResolved && this.settingsGeneralResolved.practiceName) || '';
      if (fromSettings.trim()) return fromSettings;
      return (this.site && this.site.Name) || 'Practice';
    },
    practiceInitial() {
      const n = this.practiceName.trim();
      return (n.charAt(0) || 'P').toUpperCase();
    },
    /**
     * Practice logo from teamio.sites.LogoUrl. Empty string when missing;
     * template falls back to the initial-circle in that case. Image is
     * size-constrained via CSS (object-fit: contain) so oversized files
     * scale down without blowing out the header.
     */
    practiceLogoUrl() {
      return (this.site && this.site.LogoUrl) || '';
    },
    practiceTagline() {
      return (this.settingsGeneralResolved && this.settingsGeneralResolved.practiceTagline) || '';
    },

    saveIconClass() {
      switch (this.saveStatus) {
        case 'saving': return 'fa-circle-o-notch fa-spin';
        case 'saved': return 'fa-check-circle';
        case 'error': return 'fa-exclamation-circle';
        default: return 'fa-check-circle';
      }
    },
    /**
     * Short and stable. Two labels changed on 2026-08-05:
     *
     *  - "Synced with OD" → "Saved". It claimed exactly what RefreshPlanButton's
     *    own comment says we must never claim — that Open Dental is in sync —
     *    when all this actually tracks is whether the TC's edits persisted to
     *    Teamio. It was also the state on screen most of the time, sitting
     *    beside a Refresh pill saying "checked", so the two read as arguing.
     *  - "Save failed — retry" → "Save failed". The chip isn't clickable; the
     *    word "retry" implied it was.
     *
     * Short labels are also what let the reserved width drop from 152px to 92px.
     */
    saveLabel() {
      switch (this.saveStatus) {
        case 'saving': return 'Saving…';
        case 'error': return 'Save failed';
        default: return 'Saved';
      }
    },
    saveTooltip() {
      return this.saveStatus === 'error'
        ? 'Your last change was not saved. Edit again to retry.'
        : 'Your edits to this plan are saved in Teamio';
    },

    /** Active plan first, then most-recent — same ordering as PatientTxPlansModal. */
    sortedPlans() {
      return [...this.plans].sort((a, b) => {
        if (a.status !== b.status) return (b.status || 0) - (a.status || 0);
        const ad = a.dateTP ? new Date(a.dateTP).getTime() : 0;
        const bd = b.dateTP ? new Date(b.dateTP).getTime() : 0;
        return bd - ad;
      });
    },
    selectedPlan() {
      return this.plans.find((p) => p.treatPlanNum === this.selectedTreatPlanNum) || null;
    },
    selectedPlanHeading() {
      return this.selectedPlan ? this.planLabel(this.selectedPlan) : 'Treatment plan';
    },
    selectedPlanIsActive() {
      return !!(this.selectedPlan && this.selectedPlan.status === 1);
    },
    /** 1-based position in the switcher's own ordering, so "2 of 5" matches the menu. */
    selectedPlanIndex() {
      const i = this.sortedPlans.findIndex((p) => p.treatPlanNum === this.selectedTreatPlanNum);
      return i === -1 ? 1 : i + 1;
    },

    paneGridStyle() {
      return {
        gridTemplateColumns: `${this.paneWidths.left}px 4px 1fr 4px ${this.paneWidths.right}px`,
      };
    },

    patientInitials() {
      if (!this.patient) return '';
      const f = (this.patient.fName || '').charAt(0);
      const l = (this.patient.lName || '').charAt(0);
      return (f + l).toUpperCase() || '??';
    },
    patientAvatarStyle() {
      const init = this.patientInitials;
      const hash = (init.charCodeAt(0) || 0) + (init.charCodeAt(1) || 0);
      const hues = [186, 214, 262, 28, 170, 330, 42, 200, 280];
      const h = hues[hash % hues.length];
      return { background: `oklch(0.68 0.11 ${h})` };
    },
  },

  watch: {
    siteID: {
      immediate: true,
      handler(newSite) {
        if (newSite) this.bootstrap();
      },
    },
    // Slice 8e — start/stop the 10s polling loop based on whether the
    // Comments panel is open. Polling skips while the panel is closed
    // because the thread isn't visible, so there's nothing to refresh.
    // The dashboard badge will catch up on next dashboard fetch / on next
    // panel open (which fires a fresh loadThread + drains read-by-office).
    commentsPanelOpen: {
      immediate: true,
      handler(open) {
        // `immediate` + a store-persisted panelOpen means this can fire before
        // settings have hydrated; the feature check keeps a disabled practice
        // from starting a 10s poll loop against an endpoint that returns nothing.
        if (open && this.commentsFeatureEnabled) this.startThreadPolling();
        else this.stopThreadPolling();
      },
    },
    /**
     * Settings hydrate AFTER bootstrap() — loadSettings is dispatched
     * non-awaited (`.then()`), so on a cold Builder open commentsFeatureEnabled
     * is false at the moment bootstrap fires its badge-priming loadThreadBuilder,
     * and the store guard correctly no-ops it. Without this watcher the Comments
     * button would then appear with no count until the TC clicked it.
     */
    commentsFeatureEnabled(on) {
      if (on) {
        if (this.builder.treatPlanNum) {
          this.$store.dispatch('txPresenter/loadThreadBuilder', {
            siteID: this.siteID,
            patNum: this.builder.patNum,
            treatPlanNum: this.builder.treatPlanNum,
          }).catch(() => {});
        }
        if (this.commentsPanelOpen) this.startThreadPolling();
      } else {
        // Turned off mid-session (or resolved off once settings landed): kill the
        // poll loop and close the panel so the TC isn't left looking at a thread
        // whose replies the backend will now refuse.
        this.stopThreadPolling();
        this.$store.dispatch('txPresenter/closeCommentsPanel');
      }
    },
  },

  mounted() {
    this.restorePaneWidths();
    document.addEventListener('mousemove', this.onResizeMove);
    document.addEventListener('mouseup', this.onResizeEnd);

    // Slice 8e — Page Visibility integration. When the tab is hidden we
    // pause polling (saves Lambda invocations on backgrounded tabs); when
    // the tab regains focus we restart and immediately fire one poll so
    // the TC sees up-to-date thread state on tab-return.
    //
    // It now ALSO drives the refresh-from-OD on tab-return (see below), which
    // is why the comments-panel early-return moved inside: the refresh has to
    // run whether or not the Comments panel happens to be open.
    this.threadPollVisibilityHandler = () => {
      if (document.hidden) {
        this.hiddenSince = Date.now();
        if (this.commentsPanelOpen) this.stopThreadPolling();
        return;
      }

      // Feature check as well as panel check — this handler is the one path that
      // RESTARTS polling without going through the commentsPanelOpen watcher.
      if (this.commentsPanelOpen && this.commentsFeatureEnabled) {
        this.startThreadPolling({ pollImmediately: true });
      }

      // The dominant flow behind stale plans is: alt-tab to Open Dental, add a
      // procedure, alt-tab back. Catching it here means the TC doesn't have to
      // remember to click anything for the single most common case.
      //
      // Safe to fire unprompted: every Builder edit dispatches saveAnnotations
      // immediately (there is no unsaved local state), and refreshBuilderFromOd
      // is serialized behind the save chain so it can't stomp a write in
      // flight. Builder only — Present Mode never auto-applies, because prices
      // shifting unbidden in front of a patient is worse than stale ones.
      const awayMs = this.hiddenSince ? Date.now() - this.hiddenSince : 0;
      this.hiddenSince = null;
      if (awayMs >= this.REFOCUS_REFRESH_MIN_AWAY_MS && this.builder.treatPlanNum && !this.loading) {
        this.onRefreshFromOd();
      }
    };
    document.addEventListener('visibilitychange', this.threadPollVisibilityHandler);
  },

  beforeDestroy() {
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
    if (this.threadPollVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.threadPollVisibilityHandler);
      this.threadPollVisibilityHandler = null;
    }
    this.stopThreadPolling();
    this.closeBuilder();
  },

  methods: {
    ...mapActions('txPresenter', [
      'loadBuilder', 'refreshBuilderFromOd', 'reorderProcedures',
      'setHiddenPriorityDefNums', 'setBuilderGroupBy', 'closeBuilder',
      'fetchPresentationDetail',
      'attachAlts', 'detachAlt', 'removeProcedure', 'restoreProcedure',
      'regenerateProcedureContent', 'regenerateAllProcedureContent', 'setCodeOverride',
      'acknowledgeNewProcedure',
    ]),

    builderHasOverride(proc, field) {
      return this.$store.getters['txPresenter/builderHasOverride'](proc, field);
    },
    isRegen(codeNum, type) {
      return !!this.builder.procedureContent.regenerating[`${codeNum}:${type}`];
    },
    async onBulkRegen(type) {
      if (!window.confirm(`Re-generate ${type === 'label' ? 'labels' : 'reasons'} for every procedure on this plan? This will overwrite the AI cache for these CDT codes practice-wide.`)) return;
      try {
        await this.regenerateAllProcedureContent({ type });
      } catch (e) { /* in state */ }
    },

    // Slice 10a #8 — concurrent-edit conflict actions.
    onAdoptConflict() {
      this.$store.dispatch('txPresenter/adoptConflictingState');
    },
    onDismissConflict() {
      this.$store.dispatch('txPresenter/dismissConflict');
    },

    // Slice 10a #9 — retry the failed signed-PDF render.
    onRetrySignedPdf() {
      this.$store.dispatch('txPresenter/retrySignedPdf').catch(() => { /* console-logged */ });
    },

    // Slice 10a #3 — retry the initial AI content fetch when Bedrock
    // came back down between Builder open and now. Loads both label +
    // reason in parallel; on success the procedureContent.error in store
    // clears and the banner disappears.
    async onRetryAiContent() {
      this.aiContentRetrying = true;
      try {
        await Promise.all([
          this.$store.dispatch('txPresenter/loadProcedureContent', { type: 'label' }),
          this.$store.dispatch('txPresenter/loadProcedureContent', { type: 'reason' }),
        ]);
      } catch (e) { /* error stays in store */ } finally {
        this.aiContentRetrying = false;
      }
    },
    async onRowRegen(proc, type) {
      // If the user has a per-plan override, clear it first so the regenerated
      // AI content shows. Then force-regenerate for this CodeNum.
      const field = type === 'label' ? 'shortNameOverride' : 'reasonOverride';
      try {
        if (this.builderHasOverride(proc, field)) {
          await this.setCodeOverride({ codeNum: proc.codeNum, field, value: null });
        }
        await this.regenerateProcedureContent({ codeNum: proc.codeNum, type });
      } catch (e) { /* in state */ }
    },
    // Interpolate {discount}/{splits}/{months}/{apr} tokens in a payment
    // option's description against its live field values.
    paymentDesc(opt) { return interpolatePaymentDesc(opt); },
    /** Template access to the shared predicate — see utils/paymentOptions.js. */
    canTakeDownpayment(opt) { return canTakeDownpayment(opt); },
    /** Template access to the shared predicate — see utils/cherryTerms.js. */
    isCherryBacked(opt) { return isCherryBacked(opt); },

    onChangeOverride() { /* keystroke handler placeholder — actual save on blur */ },

    /**
     * Toggle one of the layer-breakdown checkboxes (UCR / Insurance /
     * Membership). Reads current state from `pricingLayers` (which is a
     * computed over annotations.pricingDisplay), flips the key, then
     * persists via `savePricingDisplay`.
     */
    onTogglePricingLayer(key) {
      const next = { ...this.pricingLayers, [key]: !this.pricingLayers[key] };
      this.savePricingDisplay({ layers: next });
    },

    /**
     * Set one view's pricing mode: 'full' | 'total' | 'none'.
     *
     * buildPricingModePatch writes the legacy viewVisibility boolean alongside
     * the mode on purpose — see its doc comment. Don't "simplify" it to write
     * only `pricing`: patient links are long-lived and a cached older bundle
     * reads viewVisibility, where the safe failure is showing LESS money.
     */
    onSetPricingMode(view, mode) {
      // Patch from the RAW saved blob, not resolvedDisplay. resolvedDisplay has
      // both views materialized, so patching from it would write an explicit
      // value for the view the TC didn't touch — freezing it against future
      // changes to the practice default. Raw keeps "unset = inherit" intact.
      const raw = (this.builder.annotations && this.builder.annotations.pricingDisplay) || {};
      this.savePricingDisplay(buildPricingModePatch(raw, view, mode));
    },

    /**
     * Toggle the Itemized-only "Group by priority" flag.
     */
    onToggleItemizedGrouping() {
      this.savePricingDisplay({ itemizedGroupByPriority: !this.itemizedGroupByPriority });
    },

    /**
     * Toggle diagnostic (exams & X-rays) visibility for this patient.
     */
    onToggleDiagnostic() {
      this.savePricingDisplay({ showDiagnostic: !this.showDiagnostic });
    },

    /** Toggle the per-group subtotal footer (grouped Itemized). */
    onToggleGroupSubtotals() {
      this.savePricingDisplay({ itemizedGroupSubtotals: !this.itemizedGroupSubtotals });
    },

    /**
     * Toggle "Show balance/credits from Open Dental" — renders the Remaining
     * Balance / Credits line and folds the guarantor's account balance into the
     * Estimated Patient Portion. Persisted per-patient like the other layers.
     */
    onToggleShowBalance() {
      this.savePricingDisplay({ showBalance: !this.showBalance });
    },

    // ─── Photo Attachments ───────────────────────────────────────────────
    onToggleShowPhotos() {
      this.savePricingDisplay({ showPhotos: !this.showPhotos });
    },
    /** Camera icon in a tooth cell → open that tooth's photos. */
    onOpenToothPhotos(toothNum) {
      const forTooth = photosForTooth(this.visiblePlanPhotos, toothNum);
      if (forTooth.length === 0) return;
      this.lightboxPhotos = forTooth;
      this.lightboxIndex = 0;
    },
    /** Thumbnail in the manager → open that one photo full size. */
    onPreviewPhoto(photo) {
      const idx = this.patientPhotos.findIndex((p) => p.photoId === photo.photoId);
      this.lightboxPhotos = this.patientPhotos.slice();
      this.lightboxIndex = idx === -1 ? 0 : idx;
    },
    /**
     * A photo's presigned URL aged out while this page sat open. Re-listing
     * re-signs the whole library in one call; the lightbox re-renders off the
     * refreshed objects via its `photos` watcher.
     */
    async onRefreshPhotoUrls() {
      if (!this.builder.siteID || !this.builder.patNum) return;
      const openIds = this.lightboxPhotos.map((p) => p.photoId);
      await this.$store.dispatch('txPresenter/loadPatientPhotos', {
        siteID: this.builder.siteID,
        patNum: this.builder.patNum,
        force: true,
      });
      this.lightboxPhotos = this.patientPhotos.filter((p) => openIds.indexOf(p.photoId) !== -1);
    },

    /** Set the group-header word (preset or custom). Empty falls back to Priority. */
    onSetGroupLabel(word) {
      const w = (word == null ? '' : String(word)).trim();
      this.savePricingDisplay({ itemizedGroupLabel: w || 'Priority' });
    },

    /**
     * Merge a partial pricingDisplay update into annotations and save.
     * All fields are optional — missing ones preserve their existing values.
     * Uses the same saveAnnotations path as the rest of the Builder so
     * conflict-detection and the save-status indicator stay consistent.
     */
    // NOTE: this signature is a WHITELIST — a key absent from it is silently
    // dropped. That is how "Total only" shipped broken: buildPricingModePatch
    // emitted { pricing, viewVisibility } and only viewVisibility survived, so
    // the mode round-tripped through the legacy boolean and came back 'none'.
    // Adding a key to pricingDisplay means adding it HERE too; there is a test
    // pinning the patch builder's keys against this signature.
    savePricingDisplay({ pricing, layers, viewVisibility, itemizedGroupByPriority, showDiagnostic, itemizedGroupLabel, itemizedGroupSubtotals, showBalance, showPhotos } = {}) {
      const annotations = { ...(this.builder.annotations || {}) };
      const existing = annotations.pricingDisplay || {};
      annotations.pricingDisplay = {
        ...existing,
        ...(pricing ? { pricing } : {}),
        ...(layers ? { layers } : {}),
        ...(viewVisibility ? { viewVisibility } : {}),
        ...(itemizedGroupByPriority !== undefined ? { itemizedGroupByPriority } : {}),
        ...(showDiagnostic !== undefined ? { showDiagnostic } : {}),
        ...(itemizedGroupLabel !== undefined ? { itemizedGroupLabel } : {}),
        ...(itemizedGroupSubtotals !== undefined ? { itemizedGroupSubtotals } : {}),
        ...(showBalance !== undefined ? { showBalance } : {}),
        ...(showPhotos !== undefined ? { showPhotos } : {}),
      };
      this.$store.commit('txPresenter/SET_BUILDER_ANNOTATIONS', annotations);
      this.$store.dispatch('txPresenter/saveAnnotations', { annotations });
    },

    /**
     * Apply a single field change to a payment option and persist the
     * full options array as a per-patient override. First-time edits
     * materialize a deep clone of the practice defaults, then layer the
     * change in. Subsequent edits operate on the persisted override.
     *
     * Field whitelist guards against template mistakes — only fields the
     * UI actually surfaces are settable from here.
     */
    // Read-only summary of a finance option's tiers for the Builder config row,
    // one line per group (deferred / reduced-APR). Tiers are practice-wide
    // (Settings → Payment Options), not per-patient.
    financeSummaryLines(opt) {
      // A Cherry-backed option has no typed tiers to list - they are not what the patient sees.
      if (isCherryBacked(opt)) return ["Cherry's real terms — priced per patient"];
      const ints = (arr) => (Array.isArray(arr) ? arr : []).map((m) => parseInt(m, 10)).filter((m) => Number.isFinite(m) && m > 0);
      const def = ints(opt && opt.deferredMonths);
      const aprM = ints(opt && opt.aprMonths);
      const apr = parseFloat(opt && opt.apr) || 0;
      const lines = [];
      if (def.length) lines.push(`${def.join(', ')} mo deferred (0%)`);
      if (aprM.length) lines.push(`${aprM.join(', ')} mo @ ${apr}% APR`);
      if (!lines.length && opt) {
        const m = parseInt(opt.months, 10); // legacy single-term fallback
        if (Number.isFinite(m)) lines.push(apr > 0 ? `${m} mo @ ${apr}% APR` : `${m} mo deferred (0%)`);
      }
      return lines.length ? lines : ['No tiers configured'];
    },
    /**
     * A plan checkbox in the Cherry plan list: hide / show that plan for THIS patient only.
     * Writes `cherryHiddenPlansPatient` through the same override channel as the fee, so it is
     * saved with the patient's payment options, shown by Present Mode + the patient link, and
     * frozen into the signed PDF at signing. It is a SEPARATE field from the practice's
     * `cherryHiddenPlans` on purpose: the practice list re-overlays from live Settings on every
     * read (and would wipe a per-patient entry), the patient list never does.
     *
     * The Builder can hide MORE for a patient but cannot un-hide a plan Settings hid (D3): the
     * checkbox is disabled for those rows, and this refuses the toggle regardless.
     */
    onCherryPlanToggle(idx, { key, shown }) {
      const opt = this.paymentOptions[idx];
      if (!opt || !isCherryBacked(opt)) return;
      const k = String(key || '').trim().toUpperCase();
      if (!k || normalizeHiddenPlanKeys(opt.cherryHiddenPlans).indexOf(k) !== -1) return;
      this.onPaymentOptionChange(idx, 'cherryHiddenPlansPatient', toggleHiddenPlanKey(opt.cherryHiddenPlansPatient, k, !!shown));
    },
    onPaymentOptionChange(idx, field, value) {
      const ALLOWED = new Set(['enabled', 'discount', 'splits', 'months', 'feeAmount', 'downpayment', 'cherryHiddenPlansPatient']);
      if (!ALLOWED.has(field)) return;
      const next = JSON.parse(JSON.stringify(this.paymentOptions));
      if (!next[idx]) return;
      next[idx] = { ...next[idx], [field]: value };
      this.$store.commit('txPresenter/SET_BUILDER_PAYMENT_OPTION_OVERRIDES', next);

      // Editing the downpayment of the option the TC has already CHOSEN must
      // also move the plan-level `chosenDownpayment` — that scalar (not the
      // per-option field) is what the tier math, the deposit panel, the patient
      // view and the signed PDF all read. Without this the Builder would show
      // one amount and the PDF another.
      //
      // Both fields go in ONE dispatch on purpose: saveAnnotations commits all
      // three columns back from the server read-back, so a second dispatch
      // would revert the first one's local state (and lose it outright if the
      // second request failed). The action and the backend upsert both take
      // annotations + paymentOptionOverrides together, atomically.
      const pricingDisplay = (this.builder.annotations && this.builder.annotations.pricingDisplay) || {};
      if (field === 'downpayment' && next[idx].id && next[idx].id === pricingDisplay.chosenPaymentOptionId) {
        const annotations = { ...(this.builder.annotations || {}) };
        annotations.pricingDisplay = {
          ...pricingDisplay,
          chosenDownpayment: Math.max(0, parseFloat(value) || 0),
        };
        this.$store.commit('txPresenter/SET_BUILDER_ANNOTATIONS', annotations);
        this.$store.dispatch('txPresenter/saveAnnotations', { annotations, paymentOptionOverrides: next });
        return;
      }

      this.$store.dispatch('txPresenter/saveAnnotations', { paymentOptionOverrides: next });
    },

    /**
     * Revert per-patient overrides — null out the column so practice
     * defaults take over again on next render. The confirm guard exists
     * because this discards real per-patient work (discount %, terms);
     * one accidental click could blow it away.
     */
    onResetPaymentOverrides() {
      if (!this.hasPaymentOverrides) return;
      const name = this.patient ? this.patient.fName : 'this patient';
      if (!window.confirm(`Reset ${name}'s payment options back to your practice defaults? This discards any custom discount or terms you set for them.`)) return;
      this.$store.commit('txPresenter/SET_BUILDER_PAYMENT_OPTION_OVERRIDES', null);
      this.$store.dispatch('txPresenter/saveAnnotations', { paymentOptionOverrides: null });
    },

    // ─── Custom per-patient payment options ───────────
    // A custom option is a one-off plan (type 'custom') stored only in this
    // patient's PaymentOptionOverrides — never touches the practice config.
    // type 'custom' has no structured amount math; the patient card shows
    // title / desc / terms without a computed payment figure.
    isCustomOption(opt) {
      return !!opt && (opt.type === 'custom' || String(opt.id || '').startsWith('custom_'));
    },
    onAddCustomOption() {
      this.customOptionEditing = null;
      this.customOptionModalOpen = true;
    },
    onEditCustomOption(opt) {
      this.customOptionEditing = opt;
      this.customOptionModalOpen = true;
    },
    onCloseCustomOption() {
      this.customOptionModalOpen = false;
      this.customOptionEditing = null;
    },
    onSaveCustomOption({ id, title, desc, terms }) {
      // Materialize the current options (practice defaults clone or existing
      // overrides) so adding a custom option also pins the rest per-patient.
      const next = JSON.parse(JSON.stringify(this.paymentOptions));
      if (id) {
        // Edit — find by id and update the editable fields.
        const target = next.find((o) => o.id === id);
        if (target) {
          target.title = title;
          target.desc = desc;
          target.terms = terms;
        }
      } else {
        next.push({
          id: 'custom_' + Date.now(),
          type: 'custom',
          title,
          desc,
          terms,
          enabled: true,
          recommended: false,
        });
      }
      this.$store.commit('txPresenter/SET_BUILDER_PAYMENT_OPTION_OVERRIDES', next);
      this.$store.dispatch('txPresenter/saveAnnotations', { paymentOptionOverrides: next });
      this.onCloseCustomOption();
    },
    onDeleteCustomOption(idx) {
      const next = JSON.parse(JSON.stringify(this.paymentOptions));
      if (!next[idx]) return;
      const name = next[idx].title || 'this option';
      if (!window.confirm(`Remove "${name}" from ${this.patient ? this.patient.fName : 'this patient'}'s plan?`)) return;
      next.splice(idx, 1);
      this.$store.commit('txPresenter/SET_BUILDER_PAYMENT_OPTION_OVERRIDES', next);
      this.$store.dispatch('txPresenter/saveAnnotations', { paymentOptionOverrides: next });
    },

    async onBlurOverride(proc, field, newValue, currentResolved) {
      const trimmed = (newValue || '').trim();
      // If the user typed the same thing the resolver returns (i.e., AI/fallback content),
      // don't create an override — keep the AI content "live" so cache regen flows through.
      if (trimmed === (currentResolved || '').trim() && !this.builderHasOverride(proc, field)) return;
      // If they cleared the field and there was an override, clear it (revert to AI/fallback).
      if (!trimmed && this.builderHasOverride(proc, field)) {
        try { await this.setCodeOverride({ codeNum: proc.codeNum, field, value: null }); } catch (e) { /* in state */ }
        return;
      }
      // Otherwise persist the override
      if (trimmed && trimmed !== (currentResolved || '').trim()) {
        try { await this.setCodeOverride({ codeNum: proc.codeNum, field, value: trimmed }); } catch (e) { /* in state */ }
      }
    },

    async bootstrap() {
      const patNum = parseInt(this.$route.params.patNum, 10);
      const treatPlanNum = parseInt(this.$route.params.treatPlanNum, 10);
      if (!patNum || !treatPlanNum || !this.siteID) return;

      this.selectedTreatPlanNum = treatPlanNum;

      try {
        // Load settings in background — non-blocking; right-pane payment cards
        // and preview header pick up real practice config when available.
        this.$store.dispatch('txPresenter/loadSettings', { siteID: this.siteID })
          .then(() => {
            // Resolve the initial view from the practice's enabled views +
            // default layout (coerces legacy 'phase', guarantees an enabled view).
            // settingsPresentationResolved, NOT settingsGeneralResolved:
            // enabledViews/defaultView moved to the presentation section, with
            // `general` kept only as a soft-migration fallback. Reading general
            // here meant a practice could change "Default layout" in Settings
            // and the Builder would keep opening on the stale value, while
            // Present Mode and the patient view honoured it.
            this.activeView = resolveDefaultView(this.settingsPresentationResolved);
          })
          .catch(() => { /* in state */ });

        // Pull header context (patient + all plans) via the existing detail endpoint.
        // It also returns the persisted internalNote — seed the right-pane Notes tab from it.
        const detail = await this.fetchPresentationDetail({
          siteID: this.siteID,
          patNum,
          treatPlanNum,
        });
        this.patient = detail.patient;
        this.plans = detail.plans || [];
        this.internalNoteEdit = detail.internalNote || '';
        this.lastSavedNote = detail.internalNote || '';

        // Then load Builder workspace data (procedures + annotations + priorities).
        await this.loadBuilder({ siteID: this.siteID, patNum, treatPlanNum });

        // Slice 6 — fire AI label/reason loads in parallel. Cache-first on the
        // backend, so subsequent Builder opens are basically free. Non-blocking:
        // views render with abbrDesc fallbacks until content arrives.
        this.$store.dispatch('txPresenter/loadProcedureContent', { type: 'label' }).catch(() => {});
        this.$store.dispatch('txPresenter/loadProcedureContent', { type: 'reason' }).catch(() => {});

        // Photo Attachments — load the library up front, not on modal open, so
        // the camera icons and the right-panel counts are right the first time
        // the TC looks at the plan. Cached per patient in the store, so
        // switching plans for the same patient costs nothing. Non-blocking:
        // the action swallows its own errors into store state.
        this.$store.dispatch('txPresenter/loadPatientPhotos', {
          siteID: this.siteID, patNum,
        });

        // Slice 8d (count badge fix) — fetch thread now so the Comments
        // button shows an accurate count *before* the user opens the panel.
        // Non-blocking: the badge is a quality-of-life indicator, not a
        // gate, so we don't await it.
        this.$store.dispatch('txPresenter/loadThreadBuilder', {
          siteID: this.siteID, patNum, treatPlanNum,
        }).catch(() => {});
      } catch (e) { /* error in store */ }
    },

    async onSaveNote() {
      const trimmed = (this.internalNoteEdit || '').trim();
      const last = (this.lastSavedNote || '').trim();
      if (trimmed === last) return;
      this.noteSaving = true;
      try {
        await this.$store.dispatch('txPresenter/saveInternalNote', {
          internalNote: trimmed || null,
          setBy: this.$store.state.customer.employeeID || null,
        });
        this.lastSavedNote = this.internalNoteEdit;
      } catch (e) { /* in store */ } finally {
        this.noteSaving = false;
      }
    },

    async onSelectPlan(treatPlanNum) {
      if (treatPlanNum === this.selectedTreatPlanNum) return;
      this.selectedTreatPlanNum = treatPlanNum;
      this.$router.replace({
        name: 'TxPresenterBuilder',
        params: { patNum: this.$route.params.patNum, treatPlanNum },
      });
      try {
        const patNum = parseInt(this.$route.params.patNum, 10);
        await this.loadBuilder({ siteID: this.siteID, patNum, treatPlanNum });
        // Slice 8d — keep Comments count badge in sync when TC switches plans.
        this.$store.dispatch('txPresenter/loadThreadBuilder', {
          siteID: this.siteID, patNum, treatPlanNum,
        }).catch(() => {});
      } catch (e) { /* in state */ }
    },

    onReorder(newOrder) {
      // newOrder is array of procNum reflecting new visual order.
      // Persisted via store; saveStatus drives the indicator in the top bar.
      this.reorderProcedures({ newOrder }).catch(() => { /* in state */ });
    },

    onSetHiddenPriorities(defNums) {
      this.setHiddenPriorityDefNums(defNums);
    },

    onSetGroupBy(mode) {
      this.setBuilderGroupBy(mode);
    },

    onRefreshFromOd() {
      this.refreshFailedAt = null;
      this.refreshBuilderFromOd().catch(() => {
        // The error text itself lands in builder.error (and the error banner);
        // this only drives the pill's transient amber state.
        this.refreshFailedAt = Date.now();
      });
    },

    onOpenAltPicker(primaryProc) {
      this.altPickerPrimary = primaryProc;
      this.altPickerOpen = true;
    },

    async onAttachAlts({ primaryProcNum, altProcNums }) {
      try {
        await this.attachAlts({ primaryProcNum, altProcNums });
        this.altPickerOpen = false;
        this.altPickerPrimary = null;
      } catch (e) { /* in state */ }
    },

    async onDetachAlt(alt) {
      if (!alt || !alt.procNum) return;
      try {
        await this.detachAlt({ procNum: alt.procNum });
      } catch (e) { /* in state */ }
    },

    resolveEmployeeName(employeeID) {
      if (!employeeID) return '';
      const info = this.tcLookup[employeeID];
      if (!info) return '';
      const full = `${info.fName || ''} ${info.lName || ''}`.trim();
      return full || info.initials || '';
    },

    onRemoveProcedure(proc) {
      if (!proc || proc.procNum === undefined || proc.procNum === null) return;
      this.removeConfirmProc = proc;
    },
    onCancelRemove() {
      this.removeConfirmProc = null;
    },
    async onConfirmRemove() {
      const proc = this.removeConfirmProc;
      if (!proc) return;
      this.removeConfirmProc = null;
      try {
        await this.removeProcedure({
          procNum: proc.procNum,
          removedBy: this.$store.state.customer.employeeID || null,
        });
      } catch (e) { /* in state */ }
    },
    onRestoreProcedure(proc) {
      if (!proc || proc.procNum === undefined || proc.procNum === null) return;
      this.restoreProcedure({ procNum: proc.procNum }).catch(() => { /* in state */ });
    },

    onSendPlan() {
      this.$store.dispatch('txPresenter/openSendPlan').catch(() => { /* in state */ });
    },

    // Slice 9a — enter Present Mode for the current plan. The new route
    // mounts TxPresenterPresentMode which reuses the same 3 view components
    // we render in the Builder center pane. On exit it routes back here
    // and the Builder reloads cleanly.
    onPresent() {
      if (!this.builder.treatPlanNum || !this.builder.patNum) return;
      this.$router.push({
        name: 'TxPresenterPresentMode',
        params: {
          patNum: String(this.builder.patNum),
          treatPlanNum: String(this.builder.treatPlanNum),
        },
      });
    },

    // Slice 8a — Comments slide-over toggle
    onToggleComments() {
      if (this.commentsPanelOpen) {
        this.$store.dispatch('txPresenter/closeCommentsPanel');
      } else {
        this.$store.dispatch('txPresenter/openCommentsPanel');
      }
    },

    // ─── Slice 8e — focus-only thread polling ───
    /**
     * Start the 10s setInterval that calls pollThreadBuilder. Skips entirely
     * if the page is hidden (Page Visibility API). Re-entrant — calling while
     * already polling is a no-op (the early return on existing interval).
     *
     * `pollImmediately` fires one poll synchronously before scheduling the
     * interval — used on tab-refocus so the TC sees fresh state without
     * waiting up to 10s for the next tick.
     */
    startThreadPolling({ pollImmediately = false } = {}) {
      if (this.threadPollInterval) return;
      if (document.hidden) return;
      if (pollImmediately) {
        this.$store.dispatch('txPresenter/pollThreadBuilder').catch(() => {});
      }
      this.threadPollInterval = setInterval(() => {
        if (document.hidden) return; // belt + suspenders alongside visibilitychange
        this.$store.dispatch('txPresenter/pollThreadBuilder').catch(() => {});
      }, this.THREAD_POLL_MS);
    },
    /**
     * Stop the polling interval. `keepListener` is set when the visibility
     * handler is the caller — we want the interval gone but the listener
     * stays bound so we can resume on tab-refocus.
     */
    stopThreadPolling() {
      if (this.threadPollInterval) {
        clearInterval(this.threadPollInterval);
        this.threadPollInterval = null;
      }
    },
    onCloseComments() {
      this.$store.dispatch('txPresenter/closeCommentsPanel');
    },

    // Slice 8c — TC compose handlers
    onTcDraftInput(e) {
      this.$store.dispatch('txPresenter/setTcDraft', e.target.value);
    },
    onReplaceAi({ commentID }) {
      this.$store.dispatch('txPresenter/enterReplaceMode', { aiCommentID: commentID });
    },
    // Slice 8d-1 — Approve a held AI draft, releasing it to the patient.
    async onApproveAi({ commentID }) {
      try {
        const res = await this.$store.dispatch('txPresenter/releaseAiReply', { commentID });
        this.setPatientNotify(res && res.patientNotify);
      } catch (e) { /* error already logged by action */ }
    },
    /**
     * Record how the patient notification went so the compose footer can say so.
     * A release that hit the double-click race returns no patientNotify at all —
     * nothing was sent because nothing was released, so show nothing.
     */
    setPatientNotify(notify) {
      this.handoffCopied = false;
      this.lastPatientNotify = notify || null;
      // Successes are informational; let them fade so the footer doesn't
      // accumulate chrome. The undelivered warning is sticky on purpose — it's the
      // one the TC has to act on.
      if (notify && notify.sent) {
        const shown = notify;
        setTimeout(() => {
          if (this.lastPatientNotify === shown) this.lastPatientNotify = null;
        }, 6000);
      }
    },
    /**
     * Copy the ready-to-paste notification text. Mirrors SendPlanModal.onCopy's
     * clipboard-with-execCommand-fallback, but builds its own off-screen textarea
     * since there's no input to select here. The fallback matters: at a copy-only
     * practice this button IS the delivery mechanism, so it must not dead-end on a
     * non-secure context or a denied permission.
     */
    async onCopyHandoff() {
      const text = this.notifyHandoffText;
      if (!text) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          this.copyViaTextarea(text);
        }
        this.handoffCopied = true;
      } catch (e) {
        try {
          this.copyViaTextarea(text);
          this.handoffCopied = true;
        } catch (e2) {
          console.error('Copy failed:', e2);
        }
      }
    },
    copyViaTextarea(text) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    },
    onCancelReplace() {
      this.$store.dispatch('txPresenter/cancelReplaceMode');
    },
    onClearTcComposeError() {
      this.$store.dispatch('txPresenter/clearTcComposeError');
    },
    async onTcSend() {
      if (!this.canTcSend) return;
      const body = this.threadBuilder.composing.draft;
      try {
        const res = await this.$store.dispatch('txPresenter/postTcMessage', { body });
        this.setPatientNotify(res && res.patientNotify);
      } catch (e) { /* error already in store */ }
    },

    onBack() {
      this.$router.push({ name: 'TxPresenter' });
    },

    clearError() {
      this.$store.commit('txPresenter/SET_BUILDER_ERROR', null);
    },

    // ─── Resize handles ────────────────────────────────
    startResize(edge) {
      this.resizing = edge;
      this.resizeStartX = window.event ? window.event.clientX : 0;
      this.resizeStartWidth = edge === 'left' ? this.paneWidths.left : this.paneWidths.right;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    onResizeMove(e) {
      if (!this.resizing) return;
      const delta = e.clientX - this.resizeStartX;
      if (this.resizing === 'left') {
        const next = Math.max(220, Math.min(this.resizeStartWidth + delta, 520));
        this.paneWidths.left = next;
      } else if (this.resizing === 'right') {
        const next = Math.max(260, Math.min(this.resizeStartWidth - delta, 520));
        this.paneWidths.right = next;
      }
    },
    onResizeEnd() {
      if (!this.resizing) return;
      this.resizing = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.persistPaneWidths();
    },
    persistPaneWidths() {
      try {
        localStorage.setItem('txPresenterBuilderPaneWidths', JSON.stringify(this.paneWidths));
      } catch (e) { /* localStorage may be unavailable */ }
    },
    restorePaneWidths() {
      try {
        const raw = localStorage.getItem('txPresenterBuilderPaneWidths');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            if (parsed.left) this.paneWidths.left = parsed.left;
            if (parsed.right) this.paneWidths.right = parsed.right;
          }
        }
      } catch (e) { /* no-op */ }
    },

    /** Display name for a plan row — Open Dental leaves Heading blank often. */
    planLabel(plan) {
      if (!plan) return 'Treatment plan';
      return plan.heading || (plan.status === 1 ? 'Active Plan' : 'Saved Plan');
    },

    formatMoney(n) {
      if (n === null || n === undefined) return '—';
      const num = parseFloat(n) || 0;
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.txb-app {
  --txb-bg: #f7f8fa;
  --txb-surface: #ffffff;
  --txb-surface2: #f1f3f6;
  --txb-border: #e4e7ec;
  --txb-border-strong: #cdd3dc;
  --txb-text: #0f172a;
  --txb-text-mute: #475569;
  --txb-text-dim: #94a3b8;
  --txb-accent: #0f6e63;
  --txb-accent-soft: #e6f1ef;
  --txb-accent-ink: #094840;
  --txb-shadow-sm: 0 1px 2px rgba(15,23,42,0.04);
  --txb-shadow-md: 0 2px 6px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04);

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color: var(--txb-text);
  font-size: 14px;
  line-height: 1.45;
  background: var(--txb-bg);
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  -webkit-font-smoothing: antialiased;
}
.txb-app * { box-sizing: border-box; }
.txb-tab-num { font-variant-numeric: tabular-nums; }

/* ─── Top bar ─── */
.txb-topbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 18px;
  background: var(--txb-surface);
  border-bottom: 1px solid var(--txb-border);
  position: sticky;
  top: 0;
  z-index: 20;
}
.txb-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--txb-border);
  background: var(--txb-surface);
  color: var(--txb-text-mute);
  border-radius: 8px;
  cursor: pointer;
}
.txb-icon-btn:hover {
  background: var(--txb-surface2);
}

.txb-pat {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 180px;
  /* Capped 2026-08-05. There was a floor and no ceiling, so a long patient name
     silently widened the whole no-wrap row — one of the reasons the bar
     overflowed on a 1366px laptop. */
  max-width: 220px;
}
.txb-pat-meta { min-width: 0; }
.txb-pat-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}
.txb-pat-name {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.txb-pat-sub {
  font-size: 12px;
  color: var(--txb-text-dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Plan switcher ──
   Replaced a wrapping grid of one pill per saved plan (capped at 84px and
   scrolled). That put a scrollbar inside a sticky toolbar, and it existed
   because a real patient has 58 saved plans. A scrolling MENU is an ordinary
   interaction; a scrolling toolbar is not — and the menu can carry the dates
   and status the pills never showed. */
.txb-plan-switch {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 230px;
  padding: 6px 10px;
  border: 1px solid var(--txb-border);
  background: var(--txb-surface);
  color: var(--txb-text);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s;
}
.txb-plan-switch:hover:not(:disabled),
.txb-plan-switch.active { background: var(--txb-surface2); border-color: var(--txb-accent); }
.txb-plan-switch:disabled { opacity: 0.55; cursor: not-allowed; }
.txb-plan-switch .fa-caret-down { color: var(--txb-text-dim); font-size: 12px; }
.txb-plan-switch-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.txb-plan-switch-count {
  flex-shrink: 0;
  font-size: 11px;
  color: var(--txb-text-dim);
  white-space: nowrap;
}

.txb-plan-menu { min-width: 280px; }
.txb-plan-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: transparent;
  border-radius: 7px;
  text-align: left;
  cursor: pointer;
}
.txb-plan-item:hover { background: var(--txb-surface2); }
.txb-plan-item.active { background: var(--txb-accent-soft); }
.txb-plan-item.active .fa-check { color: var(--txb-accent); margin-left: auto; }
.txb-plan-item-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.txb-plan-item-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--txb-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.txb-plan-item-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--txb-text-dim);
}
.txb-plan-item-badge {
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--txb-accent-soft);
  color: var(--txb-accent-ink);
  font-weight: 600;
}

.txb-active-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--txb-accent);
}

.txb-totals {
  margin-left: auto;
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.txb-totals-label {
  font-size: 11px;
  color: var(--txb-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.txb-totals-value {
  font-size: 18px;
  font-weight: 600;
  color: var(--txb-text);
}

/* ── Sync cluster ──
   Save status and Refresh, hairline-divided inside one border so they read as
   a single unit. They used to be two separate grey pills making opposite
   claims about Open Dental ("Synced with OD" beside "checked just now"), which
   is most of what made this bar feel noisy. */
.txb-sync-cluster {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid var(--txb-border);
  border-radius: 8px;
  overflow: hidden;
  background: var(--txb-surface);
}
.txb-sync-cluster > * + * { border-left: 1px solid var(--txb-border); }
/* The Refresh button supplies its own border/radius for Present Mode; inside
   the cluster the wrapper owns both. */
.txb-sync-cluster ::v-deep .rpb {
  border: none;
  border-radius: 0;
  min-width: 150px;
}

.txb-save-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 10px;
  background: transparent;
  color: var(--txb-text-mute);
  /* Still reserved — the labels cycle Saving… → Saved and this sits downstream
     of .txb-totals' margin-left:auto, so any width change shoves Comments /
     Send / Present sideways (a real reported bug). 92px not 152px, because the
     labels got shorter: see the saveLabel computed. */
  min-width: 92px;
  justify-content: flex-start;
  white-space: nowrap;
}
.txb-save-saving { color: var(--txb-text-mute); }
.txb-save-saved { background: #ecfdf5; color: #047857; }
.txb-save-error { background: #fef2f2; color: #b91c1c; }
.txb-save-idle { color: var(--txb-text-dim); }

.txb-top-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.txb-btn,
.txb-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--txb-border);
  background: var(--txb-surface);
  color: var(--txb-text);
  transition: background 0.1s;
}
.txb-btn:hover:not(:disabled) { background: var(--txb-surface2); }
.txb-btn:disabled,
.txb-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
.txb-btn-primary {
  background: var(--txb-accent);
  color: white;
  border-color: var(--txb-accent);
}
.txb-btn-primary:hover:not(:disabled) { background: var(--txb-accent-ink); }

/* ── Present split button ──
   The teal body keeps its direct click; the caret carries the second
   presentation mode. Present in-office is the most-used action on this screen
   and must not gain a click. On a touch-primary device the caret isn't
   rendered at all and .txb-split-main-solo restores the full radius. */
.txb-split { display: inline-flex; align-items: stretch; }
.txb-split-main {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.txb-split-main-solo {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}
.txb-split-caret {
  padding: 6px 8px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  /* Hairline separator between the two halves, so it reads as one control
     with two zones rather than two abutting buttons. */
  border-left: 1px solid rgba(255, 255, 255, 0.28);
}
.txb-split-caret.active { background: var(--txb-accent-ink); }

.txb-present-menu { min-width: 260px; }
.txb-menu-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: none;
  background: transparent;
  border-radius: 7px;
  text-align: left;
  cursor: pointer;
}
.txb-menu-item:hover { background: var(--txb-surface2); }
.txb-menu-item .fa { margin-top: 2px; color: var(--txb-accent); }
.txb-menu-item-main { display: flex; flex-direction: column; gap: 2px; }
.txb-menu-item-name { font-size: 13px; font-weight: 500; color: var(--txb-text); }
.txb-menu-item-sub { font-size: 11px; color: var(--txb-text-dim); line-height: 1.4; }

/* ─── Error banner ─── */
.txb-error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
}

/* Slice 10a #8 — Concurrent-edit conflict banner. Amber-themed (vs. the
   red error banner above) because this is "two people working at once,"
   not a hard error — the TC just needs to decide whether to adopt or
   abandon the other person's changes. */
.txb-conflict-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fef9f0;
  border-bottom: 1px solid #f1d8b1;
  color: #78350f;
  font-size: 13px;
}
.txb-conflict-banner > .fa {
  color: #b45309;
  font-size: 16px;
  flex-shrink: 0;
}
.txb-conflict-body { flex: 1; min-width: 0; }
.txb-conflict-title { font-weight: 600; }
.txb-conflict-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #92400e;
}
.txb-conflict-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* Slice 10a #9 — Failed signed-PDF retry banner. Red-themed because
   this IS a hard error from the patient's perspective (they signed,
   nothing landed at the office). Distinct from the amber conflict
   banner. */
.txb-pdf-retry-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 16px;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
  color: #7f1d1d;
  font-size: 13px;
}
.txb-pdf-retry-banner > .fa { color: #b91c1c; font-size: 16px; flex-shrink: 0; }
.txb-pdf-retry-body { flex: 1; min-width: 0; }
.txb-pdf-retry-title { font-weight: 600; }
.txb-pdf-retry-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #991b1b;
  line-height: 1.4;
}

/* ─── 3-pane workspace ─── */
.txb-workspace {
  flex: 1;
  display: grid;
  min-height: 0;
}

.txb-resizer {
  background: var(--txb-border);
  cursor: col-resize;
  transition: background 0.1s;
}
.txb-resizer:hover { background: var(--txb-border-strong); }

.txb-center,
.txb-right {
  background: var(--txb-surface);
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.txb-pane-header {
  padding: 12px 16px;
  border-bottom: 1px solid var(--txb-border);
  display: flex;
  align-items: center;
  gap: 8px;
}
.txb-pane-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--txb-text);
}
.txb-pane-status {
  margin-left: auto;
  font-size: 11px;
  color: var(--txb-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* Center pane — pane header */
.txb-pane-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.4px;
  color: var(--txb-text-mute);
  text-transform: uppercase;
}
.txb-view-switcher {
  display: inline-flex;
  background: var(--txb-surface2);
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  margin-left: 16px;
}
.txb-view-tab {
  padding: 5px 12px;
  background: transparent;
  border: none;
  font-size: 12px;
  font-weight: 500;
  color: var(--txb-text-mute);
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
  white-space: nowrap;
}
.txb-view-tab:hover { color: var(--txb-text); }
.txb-view-tab.active {
  background: white;
  color: var(--txb-accent-ink);
  box-shadow: var(--txb-shadow-sm);
}
.txb-pane-livehint {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--txb-text-dim);
  font-weight: 500;
}
.txb-pane-livehint .fa { font-size: 11px; }

.txb-center-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 28px 32px;
  background: var(--txb-bg);
}

/* White-card preview frame (matches Claude Design package) */
.txb-paper {
  max-width: 760px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.04);
  padding: 32px 36px 36px;
}

.txb-paper-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 22px;
  padding-bottom: 0;
}
.txb-practice {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.txb-practice-logo {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #0d5f54;
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  font-family: Georgia, 'Times New Roman', serif;
  flex-shrink: 0;
}
/* Enlarged logo when teamio.sites.LogoUrl is set — replaces the
   circle + name + tagline block. Most office logos already embed the
   practice name, so showing both is redundant.
   max-height keeps the header height predictable across logo aspect
   ratios; object-fit:contain scales oversized files down gracefully. */
.txb-practice-logo-large {
  max-height: 64px;
  max-width: 280px;
  object-fit: contain;
  display: block;
}
.txb-practice-meta { min-width: 0; }
.txb-practice-name {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: #0d5f54;
}
.txb-practice-tag {
  font-size: 10px;
  color: #6b7785;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-top: 2px;
  font-weight: 500;
}
.txb-paper-meta {
  text-align: right;
  font-size: 12px;
}
.txb-paper-date {
  color: #475569;
  font-weight: 500;
}
.txb-paper-ref {
  color: #94a3b8;
  font-size: 11px;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.txb-paper-greet {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 18px;
  color: #0d5f54;
  margin-bottom: 8px;
  margin-top: 4px;
}
.txb-paper-intro {
  font-size: 13.5px;
  color: #334155;
  line-height: 1.65;
  margin-bottom: 22px;
}

/* Right pane — financials + notes */
.txb-right-section {
  padding: 16px;
  border-bottom: 1px solid var(--txb-border);
}
.txb-right-section:last-child { border-bottom: none; }
.txb-right-label {
  font-size: 11px;
  color: var(--txb-text-mute);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 10px;
}
.txb-right-sub {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
  color: var(--txb-text-dim);
}
.txb-right-hint {
  font-size: 12px;
  color: var(--txb-text-dim);
  line-height: 1.5;
  margin: 0;
}
.txb-hint-indent { margin-top: 6px; padding-left: 42px; }

/* A hint sitting directly under a section label needs its own breathing room
   before the controls it describes. */
.txb-right-label + .txb-right-hint { margin: -4px 0 10px; }

/* ── Per-view pricing mode ──
   A segmented control rather than a dropdown: all three modes stay visible, so
   the TC can see that "Total only" exists. It was the option nobody could find
   before, because it wasn't an option at all. */
.txb-pricing-mode + .txb-pricing-mode { margin-top: 10px; }
.txb-pricing-mode-view {
  font-size: 13px;
  color: var(--txb-text);
  margin-bottom: 5px;
}
.txb-segmented {
  display: flex;
  border: 1px solid var(--txb-border);
  border-radius: 8px;
  overflow: hidden;
  background: var(--txb-surface);
}
.txb-segmented-btn {
  flex: 1;
  padding: 6px 4px;
  border: none;
  border-right: 1px solid var(--txb-border);
  background: transparent;
  color: var(--txb-text-mute);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.txb-segmented-btn:last-child { border-right: none; }
.txb-segmented-btn:hover:not(.active) { background: var(--txb-surface2); }
.txb-segmented-btn.active {
  background: var(--txb-accent);
  color: #ffffff;
  font-weight: 600;
}

.txb-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  font-size: 13px;
  cursor: pointer;
}
.txb-toggle-text {
  color: #1e293b;
  font-weight: 500;
}
.txb-toggle-sub {
  color: var(--txb-text-dim);
  font-weight: 400;
  font-size: 12px;
  margin-left: 2px;
}
/* Full-sentence helper text under a toggle (aligned past the 32px switch +
   10px gap), e.g. the "balance/credits" source note. */
.txb-toggle-desc {
  margin: -2px 0 6px 42px;
  font-size: 11.5px;
  line-height: 1.35;
  color: var(--txb-text-dim);
}
/* Disabled toggle row (e.g., per-group subtotals when grouping is off) */
.txb-toggle-row-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.txb-toggle-row-disabled .txb-switch { cursor: not-allowed; }

/* Group-header word picker */
.txb-grouplabel { margin-top: 10px; padding-left: 42px; }
.txb-grouplabel-caption {
  font-size: 11px;
  color: var(--txb-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
}
.txb-grouplabel-opts { display: flex; flex-wrap: wrap; gap: 6px; }
.txb-grouplabel-pill {
  border: 1px solid var(--txb-border);
  background: var(--txb-surface);
  color: #475569;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.txb-grouplabel-pill:hover { background: #f1f3f6; }
.txb-grouplabel-pill.active {
  background: #ecf5f3;
  border-color: #0d5f54;
  color: #0d5f54;
}
.txb-grouplabel-input {
  margin-top: 6px;
  width: 100%;
  box-sizing: border-box;
  padding: 6px 10px;
  border: 1px solid var(--txb-border);
  border-radius: 6px;
  font-size: 12.5px;
  color: #0f172a;
}
.txb-grouplabel-input:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.txb-grouplabel-preview {
  margin-top: 6px;
  font-size: 11.5px;
  color: var(--txb-text-dim);
}
.txb-grouplabel-preview strong { color: #0d5f54; }

/* iOS-style toggle switch */
.txb-switch {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
  flex-shrink: 0;
}
/* The input fills the switch and sits transparently on top of the slider
   so IT is the click target — no reliance on <label> click-forwarding,
   which was silently failing for the payment-option toggles (the slider
   absorbed the click and the zero-size input never received it). */
.txb-switch input[type="checkbox"] {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
}
.txb-switch-slider {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #cbd5e1;
  border-radius: 18px;
  transition: background-color 0.18s;
  cursor: pointer;
}
.txb-switch-slider::before {
  content: '';
  position: absolute;
  height: 14px;
  width: 14px;
  left: 2px;
  top: 2px;
  background: white;
  border-radius: 50%;
  transition: transform 0.18s;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
}
.txb-switch input:checked + .txb-switch-slider {
  background-color: #0d5f54;
}
.txb-switch input:checked + .txb-switch-slider::before {
  transform: translateX(14px);
}

/* Payment-option cards */
.txb-pay-card {
  border: 1px solid var(--txb-border);
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 10px;
  background: var(--txb-surface);
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
}
.txb-pay-card.active {
  background: #ecf5f3;
  border-color: #b9dcd5;
}
.txb-pay-card.dim { opacity: 0.6; }

.txb-pay-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.txb-pay-title {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 14px;
  font-weight: 600;
  color: #0d5f54;
}
.txb-pay-desc {
  font-size: 12px;
  color: #475569;
  line-height: 1.5;
  padding-left: 42px;
  margin-bottom: 6px;
}
.txb-pay-pop {
  font-size: 11px;
  color: #b45309;
  margin-top: 6px;
  margin-bottom: 6px;
  padding-left: 42px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}
.txb-pay-pop .fa { color: #d97706; font-size: 10px; }

/* Custom per-patient option — badge + edit/delete actions + terms line */
.txb-pay-custom-badge {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #0d5f54;
  background: #e6f1ef;
  border: 1px solid #b9dcd5;
  border-radius: 4px;
  padding: 1px 6px;
}
.txb-pay-card-actions {
  margin-left: auto;
  display: inline-flex;
  gap: 2px;
}
.txb-pay-icon {
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  font-size: 12px;
  padding: 4px 6px;
  border-radius: 5px;
  transition: background 0.1s, color 0.1s;
}
.txb-pay-icon:hover { background: #f1f3f6; color: #475569; }
.txb-pay-icon-danger:hover { background: #fef2f2; color: #dc2626; }
.txb-pay-terms {
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.45;
  padding-left: 42px;
  margin-bottom: 6px;
}

.txb-pay-config {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-left: 42px;
  margin-top: 4px;
}
.txb-pay-input-label {
  font-size: 11px;
  color: var(--txb-text-mute);
  margin: 0;
  font-weight: 500;
}
.txb-pay-input {
  width: 64px;
  padding: 4px 8px;
  border: 1px solid var(--txb-border);
  border-radius: 5px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  background: var(--txb-surface);
  color: var(--txb-text);
}
.txb-pay-input:focus {
  outline: none;
  border-color: var(--txb-accent);
  box-shadow: 0 0 0 2px rgba(15, 110, 99, 0.12);
}
.txb-pay-suffix {
  font-size: 11px;
  color: var(--txb-text-dim);
}
/* Read-only finance tier summary (tiers are practice-wide, edited in Settings).
   Full-width column: "Financing tiers:" label, then one line per group. */
.txb-pay-finance {
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.txb-pay-finance-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--txb-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.txb-pay-finance-line {
  font-size: 12px;
  color: var(--txb-text, #334155);
  font-variant-numeric: tabular-nums;
}
/* Cherry-backed option: when the terms were fetched + the manual re-price. */
.txb-pay-cherry {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-top: 4px;
}
.txb-pay-cherry-status {
  font-size: 11px;
  color: var(--txb-text-dim);
}
.txb-pay-cherry-refresh {
  background: transparent;
  border: 1px solid var(--txb-border-strong, #cbd5e1);
  color: var(--txb-text-mute);
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.txb-pay-cherry-refresh:hover:not(:disabled) {
  background: var(--txb-accent-soft);
  border-color: var(--txb-accent);
  color: var(--txb-accent-ink);
}
.txb-pay-cherry-refresh:disabled { opacity: 0.55; cursor: not-allowed; }

/* Additional Fee row — slightly wider input so $/% values fit cleanly. */
.txb-pay-fee .txb-pay-input { width: 80px; }
.txb-pay-fee .txb-pay-input-label { font-weight: 600; color: #92400e; }

.txb-pay-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding: 8px 12px;
  width: 100%;
  background: transparent;
  border: 1px dashed var(--txb-border-strong);
  border-radius: 8px;
  font-size: 12.5px;
  color: var(--txb-text-mute);
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s, color 0.1s;
  justify-content: center;
}
.txb-pay-add:hover:not(:disabled) {
  background: var(--txb-accent-soft);
  border-color: var(--txb-accent);
  color: var(--txb-accent-ink);
}
.txb-pay-add:disabled { opacity: 0.55; cursor: not-allowed; }

.txb-pay-reset {
  background: transparent;
  border: 1px solid #e4e7ec;
  color: #64748b;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.txb-pay-reset:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}
.txb-pay-customized-pill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 6px;
  padding: 1px 7px;
  background: var(--txb-accent-soft, #ecfdf5);
  color: var(--txb-accent-ink, #047857);
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  vertical-align: middle;
}
.txb-pay-customized-pill .fa { font-size: 9px; }

.txb-note-textarea {
  width: 100%;
  resize: vertical;
  min-height: 90px;
  padding: 8px 10px;
  border: 1px solid var(--txb-border);
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  color: var(--txb-text);
  background: var(--txb-surface);
}
.txb-note-textarea:focus {
  outline: none;
  border-color: var(--txb-accent);
  box-shadow: 0 0 0 3px rgba(15, 110, 99, 0.12);
}
.txb-note-empty {
  background: #fffbeb;
  border-color: #fde68a;
}

/* Slice 6 — Reasons tab */
.txb-bulk-regen-wrap { display: inline-flex; gap: 4px; flex-wrap: wrap; }
.txb-btn-mini {
  padding: 3px 8px !important;
  font-size: 11px !important;
}
.txb-reason-legacy-warn {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 8px;
  padding: 7px 9px;
  border-radius: 4px;
  background: #fdf3d7;
  color: #8a6d1a;
  font-size: 11px;
  line-height: 1.45;
}
/* Group headers mirror the left pane's, so the two panes read as the same list. */
.txb-reason-group-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 16px;
  background: var(--txb-bg-subtle, #f8fafc);
  border-bottom: 1px solid var(--txb-border);
  position: sticky;
  top: 0;
  z-index: 1;
}
.txb-reason-group-name {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--txb-text-mute, #64748b);
}
.txb-reason-group-count {
  font-size: 11px;
  color: var(--txb-text-mute, #64748b);
}
/* "Applies to all N × D2740" — overrides are per code, so an edit here fans out.
   Shown before the TC types, not after. */
.txb-reason-fanout {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 6px;
  font-size: 11px;
  color: #8a6d1a;
  background: #fdf3d7;
  border-radius: 4px;
  padding: 3px 7px;
}
.txb-reason-row {
  padding: 12px 16px;
  border-bottom: 1px solid var(--txb-border);
}
.txb-reason-row:last-child { border-bottom: none; }
.txb-reason-row-head {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 6px;
}
.txb-reason-code {
  font-size: 11px;
  font-weight: 600;
  color: var(--txb-text-mute);
  font-variant-numeric: tabular-nums;
}
.txb-reason-tooth {
  font-size: 10.5px;
  padding: 1px 5px;
  background: var(--txb-accent-soft);
  color: var(--txb-accent);
  border-radius: 3px;
  font-weight: 600;
}
.txb-reason-od {
  font-size: 11px;
  color: var(--txb-text-dim);
  font-style: italic;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.txb-reason-field { margin-bottom: 8px; }
.txb-reason-field:last-child { margin-bottom: 0; }
.txb-reason-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  color: var(--txb-text-mute);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}
.txb-reason-pill {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  font-size: 9.5px;
  background: #fef3c7;
  color: #b45309;
  padding: 1px 5px;
  border-radius: 999px;
}
.txb-reason-input-row {
  display: flex;
  gap: 4px;
  align-items: stretch;
}
.txb-reason-input {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid var(--txb-border);
  border-radius: 5px;
  font-size: 12.5px;
  font-family: inherit;
  color: var(--txb-text);
  background: var(--txb-surface);
  resize: vertical;
}
.txb-reason-input:focus {
  outline: none;
  border-color: var(--txb-accent);
  box-shadow: 0 0 0 2px rgba(15, 110, 99, 0.12);
}
.txb-reason-textarea { line-height: 1.45; min-height: 40px; }
.txb-reason-regen {
  width: 28px;
  flex-shrink: 0;
  border: 1px solid var(--txb-border);
  background: var(--txb-surface);
  border-radius: 5px;
  color: var(--txb-text-mute);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.txb-reason-regen:hover:not(:disabled) {
  background: var(--txb-accent-soft);
  color: var(--txb-accent);
  border-color: var(--txb-accent);
}
.txb-reason-regen:disabled { opacity: 0.55; cursor: not-allowed; }

.txb-right-stub {
  text-align: center;
  padding: 28px 16px;
  color: var(--txb-text-dim);
  font-size: 13px;
}
.txb-right-stub .fa { font-size: 22px; margin-bottom: 8px; }

/* Slice 10a #3 — AI content error banner in the Reasons right pane. */
.txb-ai-error {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  background: #fef9f0;
  border: 1px solid #f1d8b1;
  border-radius: 8px;
  margin: 0 0 12px;
}
.txb-ai-error > .fa { color: #b45309; font-size: 14px; margin-top: 2px; flex-shrink: 0; }
.txb-ai-error-body { flex: 1; }
.txb-ai-error-title {
  font-size: 13px;
  font-weight: 600;
  color: #78350f;
  margin-bottom: 2px;
}
.txb-ai-error-sub {
  font-size: 12px;
  color: #92400e;
  line-height: 1.5;
  margin-bottom: 8px;
}

/* Right pane */
.txb-tabs {
  display: flex;
  border-bottom: 1px solid var(--txb-border);
}
.txb-tab {
  flex: 1;
  padding: 10px 8px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--txb-text-mute);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.1s, border-color 0.1s;
}
.txb-tab.active {
  color: var(--txb-accent-ink);
  border-bottom-color: var(--txb-accent);
}
.txb-tab:disabled { color: var(--txb-text-dim); cursor: not-allowed; }
/* Notes-tab presence dot — signals this plan has an internal note */
.txb-tab-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--txb-accent, #0d5f54);
  margin-left: 5px;
  vertical-align: 2px;
}
.txb-right-body {
  flex: 1;
  overflow: auto;
}
/* Skeleton */
.txb-skel {
  background: linear-gradient(90deg, #eef0f3 25%, #e4e7ec 37%, #eef0f3 63%);
  background-size: 400% 100%;
  animation: txb-skel-shimmer 1.4s ease infinite;
  border-radius: 4px;
}
.txb-skel-line { height: 12px; width: 100px; margin-top: 4px; }
.txb-skel-line-sm { width: 60px; height: 10px; }
@keyframes txb-skel-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

.ml-auto { margin-left: auto; }

/* ─── Slice 8a — Comments slide-over panel ─────────────────────────── */
.txb-btn.active {
  background: #0f766e;
  color: #fff;
  border-color: #0f766e;
}
.txb-btn-count {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 700;
  background: #fff;
  color: #0f766e;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}
.txb-btn:not(.active) .txb-btn-count {
  background: #0f766e;
  color: #fff;
}

.txb-comments-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  max-width: 90vw;
  background: #fff;
  border-left: 1px solid #e5e7eb;
  box-shadow: -8px 0 24px rgba(15, 23, 42, 0.08);
  z-index: 100;
  display: flex;
  flex-direction: column;
}
.txb-comments-header {
  display: flex;
  align-items: center;
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}
.txb-comments-title {
  flex: 1;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}
.txb-comments-title .fa { margin-right: 8px; color: #0f766e; }
.txb-comments-count {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 700;
  background: #0f766e;
  color: #fff;
  border-radius: 10px;
}
.txb-comments-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.txb-comments-footer {
  padding: 10px 16px;
  font-size: 12px;
  color: #94a3b8;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}
.txb-comments-footer .fa { margin-right: 6px; }

/* Slice 8c — TC compose */
.txb-comments-compose {
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}
.txb-compose-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 8px 10px;
  background: #f5f3ff;
  border: 1px solid #e0d7fe;
  border-radius: 4px;
  font-size: 12px;
  color: #4c1d95;
}
.txb-compose-banner .fa { color: #6d28d9; }
.txb-compose-banner-cancel {
  margin-left: auto;
  background: none;
  border: none;
  color: #6d28d9;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 6px;
}
.txb-compose-banner-cancel:hover { text-decoration: underline; }

.txb-compose-textarea {
  width: 100%;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  color: #1f2937;
  border: 1px solid #d4d8de;
  border-radius: 6px;
  background: #fff;
  resize: vertical;
  min-height: 64px;
  box-sizing: border-box;
}
.txb-compose-textarea:focus {
  outline: none;
  border-color: #0f766e;
  box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
}
.txb-compose-textarea:disabled { opacity: 0.6; cursor: not-allowed; }

.txb-compose-error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  color: #991b1b;
  font-size: 12px;
}
.txb-compose-error .fa { color: #dc2626; }

/* Pre-emptive "no auto-text on this account" note — informational, sits quietly
   above the send button so the TC knows the shape of the workflow before typing. */
.txb-notify-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 10px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  color: #475569;
  font-size: 11.5px;
  line-height: 1.45;
}
.txb-notify-note .fa { color: #64748b; margin-top: 1px; }

/* Outcome of the last patient notification. The `warn` variant is the important
   one: the reply posted but nobody was told, and the TC has to relay it. */
.txb-notify-result {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.45;
}
.txb-notify-text { flex: 1 1 auto; min-width: 0; }
.txb-notify-success { background: #ecfdf5; border: 1px solid #a7f3d0; color: #065f46; }
.txb-notify-success .fa { color: #059669; }
.txb-notify-warn { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
.txb-notify-warn .fa { color: #d97706; }
.txb-notify-muted { background: #f1f5f9; border: 1px solid #e2e8f0; color: #475569; }
.txb-notify-muted .fa { color: #64748b; }

.txb-notify-copy {
  flex: 0 0 auto;
  border: 1px solid #d97706;
  background: #fff;
  color: #92400e;
  border-radius: 4px;
  padding: 4px 9px;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
}
.txb-notify-copy:hover { background: #fef3c7; }
.txb-notify-copy .fa { margin-right: 4px; color: inherit; }
.txb-notify-copy-done {
  border-color: #059669;
  color: #065f46;
  background: #ecfdf5;
}
.txb-notify-copy-done .fa { color: #059669; }

.txb-notify-dismiss {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  color: inherit;
  opacity: 0.55;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}
.txb-notify-dismiss:hover { opacity: 1; }
.txb-compose-error-dismiss {
  margin-left: auto;
  background: none;
  border: none;
  color: #991b1b;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

.txb-compose-actions {
  display: flex;
  align-items: center;
  margin-top: 8px;
}
.txb-compose-hint {
  font-size: 11px;
  color: #94a3b8;
  margin-right: auto;
}
.txb-compose-send {
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #0f766e;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.txb-compose-send:hover:not(:disabled) { background: #0d5f54; }
.txb-compose-send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.txb-compose-send .fa { margin-right: 4px; }

.txb-comments-slide-enter-active,
.txb-comments-slide-leave-active {
  transition: transform 0.25s ease;
}
.txb-comments-slide-enter,
.txb-comments-slide-leave-to {
  transform: translateX(100%);
}

/* Remove-procedure confirm modal (mirrors AltPickerModal conventions) */
.rpm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 28px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.rpm-modal {
  background: #fff;
  width: 100%;
  max-width: 460px;
  max-height: calc(100vh - 56px);
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.rpm-modal * { box-sizing: border-box; }
.rpm-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e4e7ec;
}
.rpm-title {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
}
.rpm-close {
  margin-left: auto;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #475569;
  cursor: pointer;
  flex-shrink: 0;
}
.rpm-close:hover { background: #f1f3f6; }
.rpm-body {
  padding: 16px 20px;
  overflow-y: auto;
}
.rpm-text {
  font-size: 13px;
  line-height: 1.5;
  color: #334155;
  margin: 0;
}
.rpm-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 0 0;
  padding: 9px 11px;
  background: #f7f8fa;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.45;
  color: #475569;
}
.rpm-note .fa { color: #94a3b8; margin-top: 2px; flex-shrink: 0; }
.rpm-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #e4e7ec;
  background: #fafafa;
}
.rpm-btn,
.rpm-btn-danger {
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
}
.rpm-btn:hover { background: #f1f3f6; }
.rpm-btn-danger {
  background: #dc2626;
  border-color: #dc2626;
  color: #fff;
}
.rpm-btn-danger:hover { background: #b91c1c; border-color: #b91c1c; }
</style>
